import moment from "moment-timezone";
import React from "react";
import { createPortal } from "react-dom";
import { DATE_FORMATS } from "../../../constants";
import DateInput from "../../../parts/inputs/date-input";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import { useGymTimezone } from "../../../utils/use-gym-timezone";
import { getRoleNameById } from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import BlockTimeFromDetailsModal from "./BlockTimeFromDetailsModal";
import CreateBlockTimeModal, {
  BLOCK_DATE_SINGLE,
  BLOCK_TIME_SCROLL_SELECT_DATE,
  buildBlockTimeBlackoutPayloads,
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";
import CreateClassModal from "./CreateClassModal";
import CreateFacilityModal from "./CreateFacilityModal";
import CreatePtSessionModal from "./CreatePtSessionModal";
import EventDetailsPanel from "./EventDetailsPanel";
import {
  formatAmPm,
  formatBlackoutSummaryHeadingDate,
  formatBlackoutSummaryTimeRange,
  formatFullDayBlackoutBadgeTitle,
  formatHourLabel12,
  formatMonthBlackoutPopoverTimeRange,
} from "./helpers/format";
import {
  computeDayTimelineRange,
  computeOverlapLayout,
  dayTimelinePositionStyle,
  getScrollableAncestors,
  measureMonthBlackoutPopoverPlacement,
} from "./helpers/geometry";
import MarkAttendancePtView from "./MarkAttendancePtView";
import MarkAttendanceView from "./MarkAttendanceView";
import {
  formatBookingSlotLabel,
  getPtBookingRange,
  getPtCapacitySummary,
  pickClassCapacity,
  pickClassEnrolledCount,
  ptBookingMatchesOccurrence,
} from "./pt-session-slots";
import {
  dailyWeekdaySet,
  pythonWeekdayToJsGetDay,
} from "./recurring-weekday-encoding";
import SchedulingSidebarFilters from "./SchedulingSidebarFilters";
import { OverviewCalendarSkeleton } from "./SchedulingSkeletons";
import { fetchSchedulingBookable } from "./scheduling-bookable-fetch";
import {
  canManageSchedulingRecord,
  schedulingRefId,
} from "./scheduling-event-ownership";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

/** Monday = 0 … Sunday = 6 (matches PT session API) */
function mondayIndexFromDate(d) {
  return (d.getDay() + 6) % 7;
}

function startOfWeekMonday(ref) {
  const x = new Date(ref);
  x.setHours(0, 0, 0, 0);
  const off = mondayIndexFromDate(x);
  x.setDate(x.getDate() - off);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function ymdKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function eventOnDay(e, day) {
  if (e && typeof e.dateKey === "string") return e.dateKey === ymdKey(day);
  return sameDay(e.start, day);
}

/**
 * Gym-timezone calendar-day key ("YYYY-MM-DD") for an absolute-instant Date
 * (e.g. a booking's UTC start). Falls back to the browser-local day when no
 * timezone is known.
 */
function tzDateKey(date, tz) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (tz && String(tz).includes("/")) {
    return moment(date).tz(tz).format("YYYY-MM-DD");
  }
  return ymdKey(date);
}

function tzWallClockDate(date, tz) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return date;
  if (!tz || !String(tz).includes("/")) return date;
  const m = moment(date).tz(tz);
  return new Date(
    m.year(),
    m.month(),
    m.date(),
    m.hour(),
    m.minute(),
    m.second(),
    m.millisecond()
  );
}

function geomRow(row, tz) {
  if (!row || typeof row.dateKey !== "string") return row;
  const eventTimezone = row.raw?.timezone || tz;
  return {
    ...row,
    start: tzWallClockDate(row.start, eventTimezone),
    end: tzWallClockDate(row.end, eventTimezone),
  };
}

function parseYyyyMmDd(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

function parseHhMm(str) {
  if (!str || typeof str !== "string") return { h: 9, min: 0 };
  const p = str.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!p) return { h: 9, min: 0 };
  return { h: Number(p[1]), min: Number(p[2]) };
}

/** Visible hour range for day timeline (whole hours). Includes partial blackout windows. */
function hourSlotHasEvent(rows, slotStart, slotEnd) {
  return rows.some((r) => {
    if (r.kind === "blackout_summary" || !r.start || !r.end) return false;
    return (
      r.start.getTime() < slotEnd.getTime() &&
      r.end.getTime() > slotStart.getTime()
    );
  });
}

function hourSlotHasPartialBlackout(partialRows, slotStart, slotEnd) {
  return partialRows.some((r) => {
    if (!r.start || !r.end) return false;
    return (
      r.start.getTime() < slotEnd.getTime() &&
      r.end.getTime() > slotStart.getTime()
    );
  });
}

/** Day overview toolbar next to arrows: e.g. "Sept 8, 2026 Wednesday". */
function formatDayToolbarLabel(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  let month = x.toLocaleDateString("en-US", { month: "short" });
  if (month === "Sep") month = "Sept";
  const dayNum = x.getDate();
  const year = x.getFullYear();
  const weekdayRaw = x.toLocaleDateString("en-US", { weekday: "long" });
  const weekday =
    weekdayRaw.charAt(0).toUpperCase() + weekdayRaw.slice(1).toLowerCase();
  return `${month} ${dayNum}, ${year} ${weekday}`;
}

function SchedulingToolbarCalendarIcon() {
  return (
    <img
      src="/img/new-icons/vuesax-linear-calendar-schedule.svg"
      alt=""
      width={20}
      height={20}
      className="scheduling-overview-calendar-icon-svg"
      draggable={false}
    />
  );
}

/** Bookable slot duration in minutes — used to split availability windows into individual slots. */
function pickSlotDurationMinutes(raw) {
  if (!raw || typeof raw !== "object") return null;
  const keys = [
    "slot_duration",
    "slot_duration_minutes",
    "duration_minutes",
    "booking_duration",
    "booking_duration_minutes",
    "session_duration",
    "slot_length",
  ];
  for (const k of keys) {
    const v = raw[k];
    if (v != null && !Number.isNaN(Number(v))) {
      const n = Number(v);
      if (n > 0) return n;
    }
  }
  return null;
}

/** Subtitle + full flag for class cards (overview). */
function classCapacitySubtitle(c) {
  const cap = pickClassCapacity(c);
  const enrolled = pickClassEnrolledCount(c);
  if (enrolled != null && cap != null) {
    return {
      subtitle: `${enrolled}/${cap} spots`,
      bookingFull: enrolled >= cap && cap > 0,
    };
  }
  if (cap != null) {
    return { subtitle: `${cap} spots`, bookingFull: false };
  }
  return { subtitle: undefined, bookingFull: false };
}

/**
 * Coach display for a class row (same idea as PT list: embedded coach or current user).
 */
function resolveCoachProfilePhotoUrlForClass(c, activeUser, theme) {
  const coachUser = c?.coach ?? c?.instructor ?? activeUser;
  if (coachUser?.avatar?.s3Path || coachUser?.athleteAvatar?.s3Path) {
    return getAvatarUrl(coachUser, false, theme, theme !== "dark");
  }
  const explicit = c?.coach_avatar_url ?? c?.thumbnail_url;
  if (
    typeof explicit === "string" &&
    explicit.length > 0 &&
    !explicit.includes("user-circle.svg")
  ) {
    return explicit;
  }
  return getAvatarUrl(coachUser, false, theme, theme !== "dark");
}

function resolveClassCoachLabel(c, activeUser) {
  const coachUser = c?.coach ?? c?.instructor ?? activeUser;
  const name =
    coachUser?.name?.trim?.() ||
    [coachUser?.firstName, coachUser?.lastName].filter(Boolean).join(" ") ||
    "Coach";
  return name;
}

function resolvePtCoachLabel(c, activeUser) {
  const list = Array.isArray(c?.coaches) ? c.coaches : [];
  for (const entry of list) {
    const coach = entry?.coachId ?? entry?.coach ?? entry;
    if (!coach || typeof coach !== "object") continue;
    const name =
      coach?.name?.trim?.() ||
      [coach?.firstName, coach?.lastName].filter(Boolean).join(" ");
    if (name) return name;
  }
  return resolveClassCoachLabel(c, activeUser);
}

function collectEventCoachIds(raw) {
  const ids = new Set();
  if (!raw || typeof raw !== "object") return ids;
  const add = (value) => {
    if (value == null || value === "") return;
    if (typeof value === "string" || typeof value === "number") {
      ids.add(String(value));
      return;
    }
    if (typeof value === "object") {
      const id = value._id ?? value.id;
      if (id != null && id !== "") ids.add(String(id));
    }
  };
  if (Array.isArray(raw.coaches)) {
    for (const entry of raw.coaches) {
      add(entry?.coachId ?? entry?.coach ?? entry);
    }
  }
  if (Array.isArray(raw.coach_ids)) {
    for (const id of raw.coach_ids) add(id);
  }
  add(raw.coach_id);
  add(raw.coach);
  add(raw.instructor);
  return ids;
}

/** Facilities have no assigned coaches, so they filter by creator. */
function eventMatchesCoachFilter(kind, raw, filterCoachIds) {
  if (!filterCoachIds || filterCoachIds.size === 0) return true;
  if (kind === "facility") {
    const ownerId = schedulingRefId(raw?.coachId);
    return !ownerId || filterCoachIds.has(ownerId);
  }
  for (const id of collectEventCoachIds(raw)) {
    if (filterCoachIds.has(id)) return true;
  }
  return false;
}

/**
 * Whether the viewer may edit / block / unblock / take attendance for a
 * calendar row.
 */
function canManageOverviewEvent(ev, activeUser) {
  if (!ev) return false;
  if (ev.kind === "blackout_summary") {
    const ov = ev.blackoutOverride ?? {};
    return canManageSchedulingRecord(
      { coachId: ov.coachId ?? ov.createdBy },
      activeUser
    );
  }
  return canManageSchedulingRecord(ev.raw, activeUser);
}

function resolveClassCoachAvatarList(c, activeUser, theme) {
  const list = Array.isArray(c?.coaches) ? c.coaches : [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const coach = entry?.coachId ?? entry?.coach ?? entry;
    if (!coach || typeof coach !== "object") continue;
    const id = String(coach._id ?? coach.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(getAvatarUrl(coach, false, theme, theme !== "dark"));
    if (out.length >= 3) break;
  }
  if (out.length === 0) {
    out.push(resolveCoachProfilePhotoUrlForClass(c, activeUser, theme));
  }
  return out;
}

/**
 * Expand class API document into occurrences between weekStart and weekEnd (inclusive days).
 */
function expandClassEvents(c, weekStart, weekEnd) {
  const out = [];
  const id = c._id ?? c.id;
  const title = c.name ?? "Class";
  const startTime = c.start_time ?? "09:00";
  const { h: th, min: tm } = parseHhMm(startTime);
  const durMin = Number(c.duration_minutes) || 60;
  const rec = c.recurrence_type ?? "weekly";
  const rStart = parseYyyyMmDd(c.recurrence_start_date);
  const rEnd = parseYyyyMmDd(c.recurrence_end_date);
  if (rec === "one_time" && !rStart) return out;
  if (!rStart && rec !== "daily") return out;

  // Daily can be restricted to specific weekdays; null ⇒ every day.
  const dailyWd = dailyWeekdaySet(c.recurrence_weekdays);

  for (let t = weekStart.getTime(); t <= weekEnd.getTime(); t += 86400000) {
    const day = new Date(t);
    if (rStart) {
      const startMid = new Date(rStart);
      startMid.setHours(0, 0, 0, 0);
      if (day.getTime() < startMid.getTime()) continue;
    }
    if (rEnd) {
      const endOfEnd = new Date(rEnd);
      endOfEnd.setHours(23, 59, 59, 999);
      if (day.getTime() > endOfEnd.getTime()) continue;
    }

    let ok = false;
    if (rec === "one_time") {
      ok = rStart && sameDay(day, rStart);
    } else if (rec === "daily") {
      ok = !dailyWd || dailyWd.has(day.getDay());
    } else if (rec === "weekly") {
      ok = rStart && day.getDay() === rStart.getDay();
    } else if (rec === "monthly") {
      ok = rStart && day.getDate() === rStart.getDate();
    } else {
      ok = rStart && day.getDay() === rStart.getDay();
    }
    if (!ok) continue;

    let startDt;
    if (c.timezone) {
      startDt = moment
        .tz(
          {
            year: day.getFullYear(),
            month: day.getMonth(),
            day: day.getDate(),
            hour: th,
            minute: tm,
          },
          c.timezone
        )
        .toDate();
    } else {
      startDt = new Date(day);
      startDt.setHours(th, tm, 0, 0);
    }
    const endDt = new Date(startDt.getTime() + durMin * 60_000);
    const { subtitle, bookingFull } = classCapacitySubtitle(c);
    out.push({
      key: `class-${id}-${startDt.toISOString()}`,
      kind: "class",
      title,
      start: startDt,
      end: endDt,
      dateKey: ymdKey(day),
      subtitle,
      bookingFull,
      raw: c,
    });
  }
  return out;
}

const PT_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** Build `{ day_of_week, start_time, end_time }[]` from availability-windows API body. */
function windowsFromAvailabilityPayload(payload) {
  if (!payload || typeof payload !== "object") return [];
  const direct = payload.windows;
  if (Array.isArray(direct) && direct.length > 0) return direct;

  const wa = payload.weekly_availability ?? payload.weeklyAvailability;
  if (!wa || typeof wa !== "object") return [];

  const out = [];
  PT_DAY_KEYS.forEach((key, day_of_week) => {
    const cap = key.charAt(0).toUpperCase() + key.slice(1);
    const raw = wa[key] ?? wa[cap];
    if (raw == null) return;
    const rows = Array.isArray(raw) ? raw : [raw];
    for (const item of rows) {
      if (item && typeof item === "object") {
        const st = item.start_time ?? item.startTime;
        const et = item.end_time ?? item.endTime;
        if (st && et) out.push({ day_of_week, start_time: st, end_time: et });
      }
    }
  });
  return out;
}

/**
 * PT session: use embedded windows or pre-fetched availability payload.
 */
function expandPtEvents(session, availabilityPayload, weekStart, weekEnd) {
  const out = [];
  const ptSessionId = String(session._id ?? session.id ?? "pt");
  const name = session.name ?? session.title ?? "PT Session";
  let windows = [];
  if (Array.isArray(session.windows) && session.windows.length > 0) {
    windows = session.windows;
  } else {
    windows = windowsFromAvailabilityPayload(availabilityPayload);
  }

  for (let t = weekStart.getTime(); t <= weekEnd.getTime(); t += 86400000) {
    const day = new Date(t);
    const dow = mondayIndexFromDate(day);
    for (const w of windows) {
      const wd = w.day_of_week ?? w.dayOfWeek;
      if (wd == null || wd !== dow) continue;
      const st = w.start_time ?? w.startTime ?? "";
      const et = w.end_time ?? w.endTime ?? "";
      if (!st || !et) continue;
      const sh = parseHhMm(st);
      const eh = parseHhMm(et);
      let startDt;
      let endDt;
      if (session.timezone) {
        const base = {
          year: day.getFullYear(),
          month: day.getMonth(),
          day: day.getDate(),
        };
        startDt = moment
          .tz({ ...base, hour: sh.h, minute: sh.min }, session.timezone)
          .toDate();
        endDt = moment
          .tz({ ...base, hour: eh.h, minute: eh.min }, session.timezone)
          .toDate();
      } else {
        startDt = new Date(day);
        startDt.setHours(sh.h, sh.min, 0, 0);
        endDt = new Date(day);
        endDt.setHours(eh.h, eh.min, 0, 0);
      }
      if (endDt <= startDt) continue;
      out.push({
        key: `pt-${ptSessionId}-${dow}-${st}-${et}-${day.toISOString()}`,
        kind: "pt",
        title: `PT: ${name}`,
        start: startDt,
        end: endDt,
        dateKey: ymdKey(day),
        raw: session,
      });
    }
  }
  return out;
}

/**
 * Facility: weekly windows from embedded `windows` or availability-windows API (same shape as PT).
 */
function expandFacilityEvents(
  facility,
  availabilityPayload,
  weekStart,
  weekEnd
) {
  const out = [];
  const fid = String(facility._id ?? facility.id ?? "facility");
  const name = facility.name ?? facility.title ?? "Facility";
  let windows = [];
  if (Array.isArray(facility.windows) && facility.windows.length > 0) {
    windows = facility.windows;
  } else {
    windows = windowsFromAvailabilityPayload(availabilityPayload);
  }

  for (let t = weekStart.getTime(); t <= weekEnd.getTime(); t += 86400000) {
    const day = new Date(t);
    const dow = mondayIndexFromDate(day);
    for (const w of windows) {
      const wd = w.day_of_week ?? w.dayOfWeek;
      if (wd == null || wd !== dow) continue;
      const st = w.start_time ?? w.startTime ?? "";
      const et = w.end_time ?? w.endTime ?? "";
      if (!st || !et) continue;
      const sh = parseHhMm(st);
      const eh = parseHhMm(et);
      let startDt;
      let endDt;
      if (facility.timezone) {
        const base = {
          year: day.getFullYear(),
          month: day.getMonth(),
          day: day.getDate(),
        };
        startDt = moment
          .tz({ ...base, hour: sh.h, minute: sh.min }, facility.timezone)
          .toDate();
        endDt = moment
          .tz({ ...base, hour: eh.h, minute: eh.min }, facility.timezone)
          .toDate();
      } else {
        startDt = new Date(day);
        startDt.setHours(sh.h, sh.min, 0, 0);
        endDt = new Date(day);
        endDt.setHours(eh.h, eh.min, 0, 0);
      }
      if (endDt <= startDt) continue;
      const { subtitle, bookingFull } = classCapacitySubtitle(facility);
      out.push({
        key: `facility-${fid}-${dow}-${st}-${et}-${day.toISOString()}`,
        kind: "facility",
        title: name,
        start: startDt,
        end: endDt,
        dateKey: ymdKey(day),
        subtitle,
        bookingFull,
        raw: facility,
      });
    }
  }
  return out;
}

/** Map query ?preview= to internal kind. */
function normalizePreviewKind(p) {
  if (p == null || p === "") return null;
  const x = String(p).toLowerCase();
  if (x === "class" || x === "classes") return "class";
  if (x === "pt" || x === "pt-session" || x === "pt-sessions") return "pt";
  if (x === "facility" || x === "facilities") return "facility";
  return null;
}

function stripSchedulingPreviewParamsFromUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("preview") && !url.searchParams.has("previewId")) {
    return;
  }
  url.searchParams.delete("preview");
  url.searchParams.delete("previewId");
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${url.search}${url.hash}`
  );
}

/**
 * Next upcoming occurrence for a single class / PT / facility (scan forward by week).
 */
function findNextPreviewOccurrence(
  now,
  kind,
  previewId,
  classes,
  ptSessions,
  facilities,
  ptAvailability,
  facilityAvailability
) {
  const normId = String(previewId);
  const nowMs = now.getTime();
  const week0 = startOfWeekMonday(new Date(now));
  week0.setHours(0, 0, 0, 0);

  for (let w = 0; w < 104; w++) {
    const ws = addDays(week0, w * 7);
    ws.setHours(0, 0, 0, 0);
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    we.setHours(23, 59, 59, 999);

    let evs = [];
    if (kind === "class") {
      const c = classes.find((x) => String(x._id ?? x.id) === normId);
      if (!c || c.status === "cancelled" || c.status === "deleted") {
        return { event: null, reason: "not_found" };
      }
      evs = expandClassEvents(c, ws, we);
    } else if (kind === "pt") {
      const s = ptSessions.find((x) => String(x._id ?? x.id) === normId);
      if (!s || s.status === "cancelled" || s.status === "deleted") {
        return { event: null, reason: "not_found" };
      }
      const sid = s._id ?? s.id;
      evs = expandPtEvents(s, ptAvailability[sid], ws, we);
    } else if (kind === "facility") {
      const f = facilities.find((x) => String(x._id ?? x.id) === normId);
      if (!f || f.status === "cancelled" || f.status === "deleted") {
        return { event: null, reason: "not_found" };
      }
      const fid = f._id ?? f.id;
      evs = expandFacilityEvents(f, facilityAvailability[fid], ws, we);
    } else {
      return { event: null, reason: "bad_kind" };
    }

    const future = evs
      .filter((e) => e.start.getTime() >= nowMs)
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    if (future.length > 0) {
      return { event: future[0], reason: null };
    }
  }
  return { event: null, reason: "no_upcoming" };
}

function overviewStackedEventClass(kind) {
  if (kind === "pt") return " scheduling-overview-event--pt";
  if (kind === "facility") return " scheduling-overview-event--facility";
  return " scheduling-overview-event--class";
}

function overviewMonthPillClass(kind) {
  if (kind === "pt") return " scheduling-overview-month-pill--pt";
  if (kind === "facility") return " scheduling-overview-month-pill--facility";
  return " scheduling-overview-month-pill--class";
}

function groupEventsByDay(events, weekStart) {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = addDays(weekStart, i);
    const list = events
      .filter((e) => eventOnDay(e, day))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
    days.push({ date: day, events: list });
  }
  return days;
}

function startOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Availability override: `override_type === "blackout"` + bookableEventId + date range + optional times. */
function parseOverrideDayInterval(o, dayDate) {
  if (!o || o.override_type !== "blackout") return null;
  const sd = parseYyyyMmDd(o.start_date);
  const ed = parseYyyyMmDd(o.end_date);
  if (!sd || !ed) return null;
  const d = startOfLocalDay(dayDate);
  if (
    d.getTime() < startOfLocalDay(sd).getTime() ||
    d.getTime() > startOfLocalDay(ed).getTime()
  ) {
    return null;
  }
  const fullDay =
    (o.start_time == null || o.start_time === "") &&
    (o.end_time == null || o.end_time === "");
  if (fullDay) {
    const start = new Date(d);
    start.setHours(0, 0, 0, 0);
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  const st = parseHhMm(o.start_time || "00:00");
  const et = parseHhMm(o.end_time || "23:59");
  const start = new Date(d);
  start.setHours(st.h, st.min, 0, 0);
  const end = new Date(d);
  end.setHours(et.h, et.min, 0, 0);
  if (end <= start) return null;
  return { start, end };
}

function intervalsOverlapMs(a0, a1, b0, b1) {
  return a0 < b1 && b0 < a1;
}

function buildPtOccurrenceSegments(occurrence, bookings, session, sessionId) {
  const occurrenceStart = occurrence?.start?.getTime();
  const occurrenceEnd = occurrence?.end?.getTime();
  if (!Number.isFinite(occurrenceStart) || !Number.isFinite(occurrenceEnd)) {
    return [];
  }
  const bookingGroups = new Map();

  for (const booking of bookings) {
    const bookingRange = getPtBookingRange(booking);
    const bookingStart = bookingRange?.start?.getTime();
    const bookingEnd = bookingRange?.end?.getTime();
    if (!Number.isFinite(bookingStart) || !Number.isFinite(bookingEnd)) {
      continue;
    }
    if (!ptBookingMatchesOccurrence(booking, occurrence, sessionId)) {
      if (
        !intervalsOverlapMs(
          bookingStart,
          bookingEnd,
          occurrenceStart,
          occurrenceEnd
        )
      ) {
        continue;
      }
    }

    const start = Math.max(occurrenceStart, bookingStart);
    const end = Math.min(occurrenceEnd, bookingEnd);
    if (end <= start) continue;
    const key = `${start}-${end}`;
    const group = bookingGroups.get(key) ?? {
      start,
      end,
      bookings: [],
    };
    group.bookings.push(booking);
    bookingGroups.set(key, group);
  }

  const bookedGroups = [...bookingGroups.values()].sort(
    (a, b) => a.start - b.start
  );
  const segments = [];
  let cursor = occurrenceStart;

  for (const group of bookedGroups) {
    if (group.start > cursor) {
      segments.push({
        type: "free",
        start: cursor,
        end: group.start,
      });
    }
    segments.push({ type: "booked", ...group });
    cursor = Math.max(cursor, group.end);
  }
  if (cursor < occurrenceEnd) {
    segments.push({ type: "free", start: cursor, end: occurrenceEnd });
  }

  return segments.map((segment, index) => {
    const start = new Date(segment.start);
    const end = new Date(segment.end);
    if (segment.type === "free") {
      return {
        ...occurrence,
        key: `${occurrence.key}-free-${index}`,
        start,
        end,
        raw: { ...session, enrolled_count: 0, booked_members: [] },
        fromBooking: true,
      };
    }
    return {
      ...occurrence,
      key: `${occurrence.key}-booked-${segment.start}-${segment.end}`,
      start,
      end,
      raw: {
        ...session,
        enrolled_count: segment.bookings.length,
        booked_members: segment.bookings.map((booking) => booking.member),
      },
      fromBooking: true,
    };
  });
}

function buildBookingSlotEvents({ kind, record, title, bookings }) {
  const groups = new Map();
  for (const booking of bookings) {
    const slotKey = `${booking.start.getTime()}-${booking.end.getTime()}`;
    const group = groups.get(slotKey) ?? {
      slotKey,
      start: booking.start,
      end: booking.end,
      bookings: [],
    };
    group.bookings.push(booking);
    groups.set(slotKey, group);
  }
  const recordId = String(record._id ?? record.id);
  return [...groups.values()].map((group) => ({
    key: `${kind}-${recordId}-booking-${group.slotKey}`,
    kind,
    title,
    start: group.start,
    end: group.end,
    dateKey: tzDateKey(group.start, record.timezone),
    raw: {
      ...record,
      enrolled_count: group.bookings.length,
      booked_members: group.bookings.map((booking) => booking.member),
    },
    fromBooking: true,
  }));
}

/** Normalize API `schedule_type` (case, hyphenated variants). */
function normalizeCoachBlackoutScheduleType(b) {
  const raw = b?.schedule_type;
  if (raw == null || raw === "") return null;
  const s = String(raw).trim().toLowerCase().replace(/-/g, "_");
  if (s === "singleday") return "single_day";
  if (s === "daterange") return "date_range";
  return s;
}

/**
 * Calendar bounds on coach blackout docs: flat `calendar_*`, or nested `date_range`, etc.
 */
function coachBlackoutRangeYmd(b) {
  if (!b || typeof b !== "object") return { start: null, end: null };
  const start =
    b.calendar_start_date ??
    b.date_range?.start_date ??
    b.range_start_date ??
    b.start_date;
  const end =
    b.calendar_end_date ??
    b.date_range?.end_date ??
    b.range_end_date ??
    b.end_date;
  return { start, end: end ?? start };
}

/**
 * Coach blackout from GET /scheduling/blackouts — not availability overrides.
 * Supports `single_day`, `date_range` (inclusive start/end days), `recurring` + weekdays.
 * Full-day vs time window is handled in `parseCoachBlackoutDayInterval`.
 */
function coachBlackoutAppliesToDate(b, dayDate) {
  if (!b || typeof b !== "object") return false;
  const d = startOfLocalDay(dayDate);
  const t = d.getTime();
  const { start: yStart, end: yEnd } = coachBlackoutRangeYmd(b);
  let st = normalizeCoachBlackoutScheduleType(b);
  if (
    !st &&
    yStart &&
    yEnd &&
    String(yStart).slice(0, 10) !== String(yEnd).slice(0, 10)
  ) {
    st = "date_range";
  }
  if (!st && yStart) {
    st = "single_day";
  }
  if (!st) return false;

  if (st === "single_day") {
    const sd = parseYyyyMmDd(yStart);
    if (!sd) return false;
    return sameDay(d, sd);
  }
  if (st === "date_range") {
    const sd = parseYyyyMmDd(yStart);
    const ed = parseYyyyMmDd(yEnd ?? yStart ?? "");
    if (!sd || !ed) return false;
    return (
      t >= startOfLocalDay(sd).getTime() && t <= startOfLocalDay(ed).getTime()
    );
  }
  if (st === "recurring") {
    const days = b.recurring_weekdays;
    if (!Array.isArray(days) || days.length === 0) return false;
    const dow = d.getDay();
    if (!days.some((x) => pythonWeekdayToJsGetDay(x) === dow)) {
      return false;
    }
    const sd = yStart ? parseYyyyMmDd(yStart) : null;
    if (sd && t < startOfLocalDay(sd).getTime()) return false;
    const openEnded =
      b.recurring_open_ended === true ||
      b.recurring_ends === "never" ||
      b.recurring_ends === "Never";
    if (openEnded) return true;
    const endRaw = b.recurring_until_date ?? yEnd ?? b.recurring_end_date;
    const ed = endRaw ? parseYyyyMmDd(endRaw) : null;
    if (ed && t > startOfLocalDay(ed).getTime()) return false;
    return true;
  }
  return false;
}

function parseCoachBlackoutDayInterval(b, dayDate) {
  if (!coachBlackoutAppliesToDate(b, dayDate)) return null;
  const d = startOfLocalDay(dayDate);
  const isSpecific = b.time_mode === "specific_time";
  const isFullDay = b.time_mode === "full_day" || !isSpecific;
  if (isFullDay) {
    const start = new Date(d);
    start.setHours(0, 0, 0, 0);
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  const st = parseHhMm(b.time_window_start ?? b.start_time ?? "00:00");
  const et = parseHhMm(b.time_window_end ?? b.end_time ?? "23:59");
  const start = new Date(d);
  start.setHours(st.h, st.min, 0, 0);
  const end = new Date(d);
  end.setHours(et.h, et.min, 0, 0);
  if (end <= start) return null;
  return { start, end };
}

/** Per-event block time rows (`source: "single_event"`) apply only via overrides, not coach-wide overlap. */
function isSingleEventCoachBlackoutDoc(b) {
  if (!b || typeof b !== "object") return false;
  return b.source === "single_event" || b.action === "single_event";
}

function isSingleEventBlockOverride(o, coachBlackouts) {
  if (!o || o.override_type !== "blackout" || !Array.isArray(coachBlackouts)) {
    return false;
  }
  const bid = o.blackoutId ?? o.blackout_id;
  if (!bid) return false;
  const doc = coachBlackouts.find((b) => String(b._id ?? b.id) === String(bid));
  return Boolean(doc && isSingleEventCoachBlackoutDoc(doc));
}

function eventHasSingleEventBlock(ev, dayDate, overrides, coachBlackouts) {
  const o = findBlackoutOverrideForEvent(ev, dayDate, overrides);
  return Boolean(o && isSingleEventBlockOverride(o, coachBlackouts));
}

function coachBlackoutAppliesToEvent(b, ev) {
  const blackoutCoachId = schedulingRefId(b?.coachId);
  const eventCoachId = schedulingRefId(ev?.raw?.coachId);
  if (!blackoutCoachId || !eventCoachId) return true;
  return blackoutCoachId === eventCoachId;
}

function findCoachBlackoutForEvent(ev, dayDate, coachBlackouts) {
  if (!Array.isArray(coachBlackouts) || !ev) return null;
  for (const b of coachBlackouts) {
    if (isSingleEventCoachBlackoutDoc(b)) continue;
    if (!coachBlackoutAppliesToEvent(b, ev)) continue;
    const iv = parseCoachBlackoutDayInterval(b, dayDate);
    if (!iv) continue;
    if (
      intervalsOverlapMs(
        ev.start.getTime(),
        ev.end.getTime(),
        iv.start.getTime(),
        iv.end.getTime()
      )
    ) {
      return b;
    }
  }
  return null;
}

function isCoachBlackoutFullDay(b) {
  return b?.time_mode !== "specific_time";
}

function findBlackoutOverrideForEvent(ev, dayDate, overrides) {
  if (!Array.isArray(overrides) || !ev?.raw) return null;
  const bid = String(ev.raw._id ?? ev.raw.id ?? "");
  if (!bid) return null;
  for (const o of overrides) {
    if (o.override_type !== "blackout") continue;
    if (String(o.bookableEventId ?? "") !== bid) continue;
    const iv = parseOverrideDayInterval(o, dayDate);
    if (!iv) continue;
    if (
      intervalsOverlapMs(
        ev.start.getTime(),
        ev.end.getTime(),
        iv.start.getTime(),
        iv.end.getTime()
      )
    ) {
      return o;
    }
  }
  return null;
}

function isOverrideFullDay(o) {
  return (
    (o.start_time == null || o.start_time === "") &&
    (o.end_time == null || o.end_time === "")
  );
}

function buildBlackoutSummariesForDay(
  dayDate,
  overrides,
  loadedBookableIds,
  coachBlackouts
) {
  if (!Array.isArray(overrides)) return [];
  const dayTs = startOfLocalDay(dayDate).getTime();
  const fullDayRows = [];
  const partialOut = [];
  const seen = new Set();

  for (const o of overrides) {
    if (o.override_type !== "blackout") continue;
    if (
      Array.isArray(coachBlackouts) &&
      isSingleEventBlockOverride(o, coachBlackouts)
    ) {
      continue;
    }
    const bid = String(o.bookableEventId ?? "");
    if (!bid || !loadedBookableIds.has(bid)) continue;
    const iv = parseOverrideDayInterval(o, dayDate);
    if (!iv) continue;
    const oid = String(o._id ?? o.id ?? "");
    if (!oid || seen.has(oid)) continue;
    seen.add(oid);

    if (isOverrideFullDay(o)) {
      fullDayRows.push({ o, iv });
    } else {
      partialOut.push({
        kind: "blackout_summary",
        key: `blackout-summary-${oid}-${dayTs}`,
        start: iv.start,
        end: iv.end,
        blackoutOverride: o,
        fullDayOnly: false,
      });
    }
  }

  const out = [];
  if (fullDayRows.length > 0) {
    const first = fullDayRows[0];
    const fullDayTooltip = fullDayRows
      .map(({ o: ov }) => [ov?.name, ov?.reason].filter(Boolean).join(" — "))
      .filter(Boolean)
      .join(" · ");
    out.push({
      kind: "blackout_summary",
      key: `blackout-summary-fullday-${dayTs}`,
      start: first.iv.start,
      end: first.iv.end,
      blackoutOverride: first.o,
      fullDayOnly: true,
      fullDayTooltip: fullDayTooltip || undefined,
    });
  }
  out.push(...partialOut);
  return out;
}

/**
 * @param {Set<string> | null} [filterCoachIds] sidebar coach filter; when
 *   non-empty, only blackouts owned by these coaches produce strips.
 */
function buildCoachBlackoutSummariesForDay(
  dayDate,
  coachBlackouts,
  filterCoachIds = null
) {
  if (!Array.isArray(coachBlackouts)) return [];
  const dayTs = startOfLocalDay(dayDate).getTime();
  const fullDayRows = [];
  const partialOut = [];
  const seen = new Set();

  for (const b of coachBlackouts) {
    if (isSingleEventCoachBlackoutDoc(b)) continue;
    if (filterCoachIds?.size > 0) {
      const ownerId = schedulingRefId(b?.coachId);
      if (ownerId && !filterCoachIds.has(ownerId)) continue;
    }
    const iv = parseCoachBlackoutDayInterval(b, dayDate);
    if (!iv) continue;
    const oid = String(b._id ?? b.id ?? "");
    if (!oid || seen.has(oid)) continue;
    seen.add(oid);

    if (isCoachBlackoutFullDay(b)) {
      fullDayRows.push({ b, iv });
    } else {
      partialOut.push({
        kind: "blackout_summary",
        key: `blackout-summary-coach-${oid}-${dayTs}`,
        start: iv.start,
        end: iv.end,
        blackoutOverride: b,
        fullDayOnly: false,
      });
    }
  }

  const out = [];
  if (fullDayRows.length > 0) {
    const first = fullDayRows[0];
    const fullDayTooltip = fullDayRows
      .map(({ b: row }) =>
        [row?.name, row?.description].filter(Boolean).join(" — ")
      )
      .filter(Boolean)
      .join(" · ");
    out.push({
      kind: "blackout_summary",
      key: `blackout-summary-coach-fullday-${dayTs}`,
      start: first.iv.start,
      end: first.iv.end,
      blackoutOverride: first.b,
      fullDayOnly: true,
      fullDayTooltip: fullDayTooltip || undefined,
    });
  }
  out.push(...partialOut);
  return out;
}

/**
 * Override summaries and coach blackout summaries each emit one merged full-day card.
 * When both exist for the same day (common after creating a coach blackout), we would
 * show two identical "Blacked out" strips — collapse to one with a combined tooltip.
 */
function collapseFullDayBlackoutSummaryRows(summaries) {
  const fullDayRows = [];
  const rest = [];
  for (const s of summaries) {
    if (s.kind === "blackout_summary" && s.fullDayOnly) {
      fullDayRows.push(s);
    } else {
      rest.push(s);
    }
  }
  if (fullDayRows.length <= 1) return summaries;
  const tooltipParts = fullDayRows
    .map((row) => {
      if (row.fullDayTooltip) return row.fullDayTooltip;
      const o = row.blackoutOverride;
      if (!o) return "";
      if (o.override_type === "blackout") {
        return [o.name, o.reason].filter(Boolean).join(" — ");
      }
      return [o.name, o.description].filter(Boolean).join(" — ");
    })
    .filter(Boolean);
  const first = fullDayRows[0];
  const merged = {
    ...first,
    key: `blackout-summary-fullday-collapsed-${first.start.getTime()}`,
    fullDayTooltip: tooltipParts.join(" · ") || first.fullDayTooltip,
  };
  return [...rest, merged];
}

/** All blackout summary rows for a day (week merge may omit these when no event is affected). */
function getCollapsedBlackoutSummariesForDay(
  dayDate,
  overrides,
  loadedBookableIds,
  coachBlackouts,
  filterCoachIds = null
) {
  return collapseFullDayBlackoutSummaryRows([
    ...buildBlackoutSummariesForDay(
      dayDate,
      overrides,
      loadedBookableIds,
      coachBlackouts
    ),
    ...buildCoachBlackoutSummariesForDay(
      dayDate,
      coachBlackouts,
      filterCoachIds
    ),
  ]);
}

function isEventRowBlackoutAffected(ev) {
  if (ev?.singleEventBlock) return false;
  return Boolean(ev?.blackoutOverride || ev?.coachBlackout);
}

/**
 * Order: chronological events, with blackout summary strips inserted immediately after
 * the last blacked-out event. Unaffected events before that time stay above; unaffected
 * events after stay below the summary (e.g. morning blackout + strip, then afternoon events).
 */
function mergeEventsAndBlackoutSummariesInOrder(eventsSorted, summariesSorted) {
  const byTime = (a, b) => a.start.getTime() - b.start.getTime();
  const summaries = [...summariesSorted].sort(byTime);
  if (summaries.length === 0) return eventsSorted;
  let lastAffectedIdx = -1;
  for (let i = 0; i < eventsSorted.length; i++) {
    if (isEventRowBlackoutAffected(eventsSorted[i])) lastAffectedIdx = i;
  }
  const out = [];
  for (let i = 0; i < eventsSorted.length; i++) {
    out.push(eventsSorted[i]);
    if (i === lastAffectedIdx) out.push(...summaries);
  }
  if (lastAffectedIdx === -1) out.push(...summaries);
  return out;
}

function mergeDayEventsWithBlackouts(
  dayEvents,
  dayDate,
  overrides,
  loadedBookableIds,
  coachBlackouts,
  filterCoachIds = null
) {
  const annotated = dayEvents.map((ev) => {
    const o = findBlackoutOverrideForEvent(ev, dayDate, overrides);
    const coachB = findCoachBlackoutForEvent(ev, dayDate, coachBlackouts);
    const singleEventBlock = Boolean(
      o && isSingleEventBlockOverride(o, coachBlackouts)
    );
    return {
      ...ev,
      blackoutOverride: o,
      coachBlackout: coachB,
      singleEventBlock,
    };
  });
  /** Only show "Blacked out" summary strips when at least one real (non–single-slot) event is affected. */
  const hasAnyBlackedOutEvent = annotated.some(
    (ev) => !ev.singleEventBlock && (ev.blackoutOverride || ev.coachBlackout)
  );
  const summaries = hasAnyBlackedOutEvent
    ? collapseFullDayBlackoutSummaryRows([
        ...buildBlackoutSummariesForDay(
          dayDate,
          overrides,
          loadedBookableIds,
          coachBlackouts
        ),
        ...buildCoachBlackoutSummariesForDay(
          dayDate,
          coachBlackouts,
          filterCoachIds
        ),
      ])
    : [];
  const KIND_LAYER = { facility: 0, pt: 1, class: 2 };
  const minutesOfDayInTz = (date, tz) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 0;
    const m = tz ? moment.tz(date, tz) : moment(date);
    return m.hours() * 60 + m.minutes();
  };
  const eventsSorted = [...annotated].sort((a, b) => {
    const la = KIND_LAYER[a.kind] ?? 3;
    const lb = KIND_LAYER[b.kind] ?? 3;
    if (la !== lb) return la - lb;
    const ma = minutesOfDayInTz(a.start, a.raw?.timezone);
    const mb = minutesOfDayInTz(b.start, b.raw?.timezone);
    if (ma !== mb) return ma - mb;
    return a.start.getTime() - b.start.getTime();
  });
  const summariesSorted = [...summaries].sort(
    (a, b) => a.start.getTime() - b.start.getTime()
  );
  return mergeEventsAndBlackoutSummariesInOrder(eventsSorted, summariesSorted);
}

function MonthBlackoutInfoPopover({ row, tz }) {
  const rowRef = React.useRef(null);
  const btnRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  /** null until measured — avoids fixed(0,0) flash (popover stuck to top-left of viewport). */
  const [placement, setPlacement] = React.useState(null);

  const syncPlacement = React.useCallback(() => {
    setPlacement(
      measureMonthBlackoutPopoverPlacement(rowRef.current, btnRef.current)
    );
  }, []);

  const openTooltip = React.useCallback(() => {
    setPlacement(
      measureMonthBlackoutPopoverPlacement(rowRef.current, btnRef.current)
    );
    setOpen(true);
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    syncPlacement();
    /** Second frame: layout/scroll containers (e.g. .scheduling-content) may settle after first paint. */
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) syncPlacement();
      });
    });
    const onScrollOrResize = () => syncPlacement();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onScrollOrResize);
      window.visualViewport.addEventListener("scroll", onScrollOrResize);
    }
    const el = rowRef.current || btnRef.current;
    const scrollParents = el ? getScrollableAncestors(el) : [];
    for (const node of scrollParents) {
      node.addEventListener("scroll", onScrollOrResize, true);
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onScrollOrResize);
        window.visualViewport.removeEventListener("scroll", onScrollOrResize);
      }
      for (const node of scrollParents) {
        node.removeEventListener("scroll", onScrollOrResize, true);
      }
    };
  }, [open, syncPlacement]);

  const dateLine = formatBlackoutSummaryHeadingDate(row.start);
  const timeLine = formatMonthBlackoutPopoverTimeRange(row, tz);
  const ariaLabel = `Blacked out. ${dateLine}. ${timeLine}`;
  const popoverId = `month-blackout-popover-${row.key}`;

  return (
    <>
      <div ref={rowRef} className="scheduling-overview-month-partial-blackout">
        <span className="scheduling-overview-month-partial-blackout-label">
          Blacked out
        </span>
        <button
          ref={btnRef}
          type="button"
          className="scheduling-overview-month-blackout-info"
          aria-describedby={open ? popoverId : undefined}
          aria-label={ariaLabel}
          onMouseEnter={openTooltip}
          onMouseLeave={() => setOpen(false)}
          onFocus={openTooltip}
          onBlur={() => setOpen(false)}
        >
          ?
        </button>
      </div>
      {open &&
        placement &&
        createPortal(
          <div
            className="scheduling-overview-month-blackout-popover-anchor"
            style={{
              position: "fixed",
              top: `${placement.top}px`,
              left: `${placement.left}px`,
              width: 0,
              height: 0,
              zIndex: 10050,
              pointerEvents: "none",
            }}
          >
            <div
              id={popoverId}
              className="scheduling-overview-month-blackout-popover"
              role="tooltip"
            >
              <div
                className="scheduling-overview-month-blackout-popover-caret"
                aria-hidden
              />
              <strong className="scheduling-overview-month-blackout-popover-title">
                Blacked out
              </strong>
              <span className="scheduling-overview-month-blackout-popover-date">
                {dateLine}
              </span>
              <span className="scheduling-overview-month-blackout-popover-time">
                {timeLine}
              </span>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function MonthDayExpandedPopover({
  date,
  pillRows,
  anchorRect,
  onClose,
  onEventClick,
  previewFocusKey,
}) {
  const popoverRef = React.useRef(null);
  const [style, setStyle] = React.useState({
    position: "fixed",
    top: -9999,
    left: -9999,
    zIndex: 10060,
  });

  React.useLayoutEffect(() => {
    const el = popoverRef.current;
    if (!el || !anchorRect) return;
    const GAP = 6;
    const W = el.offsetWidth || 220;
    const H = el.offsetHeight || 300;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = anchorRect.bottom + GAP;
    let left = anchorRect.left + anchorRect.width / 2 - W / 2;
    if (top + H > vh - 8) top = anchorRect.top - H - GAP;
    left = Math.max(8, Math.min(left, vw - W - 8));
    setStyle({ position: "fixed", top, left, zIndex: 10060 });
  }, [anchorRect]);

  React.useEffect(() => {
    const handleDown = (e) => {
      if (!popoverRef.current?.contains(e.target)) onClose();
    };
    document.addEventListener("pointerdown", handleDown);
    return () => document.removeEventListener("pointerdown", handleDown);
  }, [onClose]);

  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  React.useEffect(() => {
    window.addEventListener("scroll", onClose, true);
    return () => window.removeEventListener("scroll", onClose, true);
  }, [onClose]);

  const dateLabel = date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return createPortal(
    <div
      ref={popoverRef}
      className="scheduling-overview-month-day-popover"
      style={style}
    >
      <div className="scheduling-overview-month-day-popover-header">
        <span className="scheduling-overview-month-day-popover-title">
          {dateLabel}
        </span>
        <button
          type="button"
          className="scheduling-overview-month-day-popover-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="scheduling-overview-month-day-popover-events">
        {pillRows.map(({ ev, monthPillSingleEvent, monthPillBlackout }) => (
          <button
            key={ev.key}
            type="button"
            className={
              "scheduling-overview-month-pill" +
              overviewMonthPillClass(ev.kind) +
              (previewFocusKey != null && ev.key === previewFocusKey
                ? " scheduling-overview-month-pill--preview-focus"
                : "") +
              (monthPillSingleEvent
                ? " scheduling-overview-month-pill--single-event-block"
                : "") +
              (monthPillBlackout
                ? " scheduling-overview-month-pill--blackout"
                : "")
            }
            title={`${formatAmPm(ev.start, ev.raw?.timezone)} – ${formatAmPm(ev.end, ev.raw?.timezone)} · ${ev.title}`}
            onClick={() => {
              onEventClick(
                monthPillSingleEvent ? { ...ev, singleEventBlock: true } : ev
              );
              onClose();
            }}
          >
            <div
              className={
                "scheduling-overview-month-pill-body" +
                (monthPillSingleEvent
                  ? " scheduling-overview-month-pill-body--blocked-row"
                  : "")
              }
            >
              <span className="scheduling-overview-month-pill-time">
                {formatAmPm(ev.start, ev.raw?.timezone)}
              </span>
              <span className="scheduling-overview-month-pill-label">
                {ev.title}
              </span>
              {monthPillSingleEvent ? (
                <span className="scheduling-overview-month-pill-blocked-tag">
                  Blocked
                </span>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
}

/**
 * Day view: hour column + timeline track with positioned events and empty-hour "Available slot" rows.
 */
function SchedulingOverviewDayTimeline({
  dayDate,
  timelineRows,
  mergedRows,
  blockedCount,
  activeUser,
  theme,
  coachAvatarSrc,
  previewFocusKey,
  onEventClick,
  onEventEdit,
  onSlotCreate,
  hasNonPullScheduling,
  tz,
}) {
  const partialBlackouts = mergedRows.filter(
    (r) => r.kind === "blackout_summary" && !r.fullDayOnly
  );

  const geomRows = React.useMemo(
    () => timelineRows.map((r) => geomRow(r, tz)),
    [timelineRows, tz]
  );
  const geomPartials = partialBlackouts.map((r) => geomRow(r, tz));
  const geomByKey = React.useMemo(() => {
    const m = new Map();
    for (const r of geomRows) m.set(r.key, r);
    return m;
  }, [geomRows]);
  const fullDaySummaries = mergedRows.filter(
    (r) => r.kind === "blackout_summary" && r.fullDayOnly
  );

  const { startHour, endHourExclusive } = computeDayTimelineRange(
    geomRows,
    geomPartials
  );
  const numHours = Math.max(1, endHourExclusive - startHour);
  const rangeStart = new Date(dayDate);
  rangeStart.setHours(startHour, 0, 0, 0);
  const rangeEnd = new Date(dayDate);
  rangeEnd.setHours(endHourExclusive, 0, 0, 0);
  const totalMin = Math.max(
    1,
    (rangeEnd.getTime() - rangeStart.getTime()) / 60000
  );
  const rangeStartMs = rangeStart.getTime();

  const [activeSlotKey, setActiveSlotKey] = React.useState(null);

  const { colMap, totalColsMap } = React.useMemo(
    () => computeOverlapLayout(geomRows),
    [geomRows]
  );

  React.useEffect(() => {
    if (!activeSlotKey) return;
    const handle = (e) => {
      if (!e.target.closest(".scheduling-overview-day-available-slot-wrap")) {
        setActiveSlotKey(null);
      }
    };
    document.addEventListener("pointerdown", handle);
    return () => document.removeEventListener("pointerdown", handle);
  }, [activeSlotKey]);

  const eventCount = timelineRows.length;
  const eventCountByStartHour = new Map();
  for (const row of geomRows) {
    if (!(row.start instanceof Date) || Number.isNaN(row.start.getTime()))
      continue;
    const hourKey = `${startOfLocalDay(row.start).getTime()}-${row.start.getHours()}`;
    eventCountByStartHour.set(
      hourKey,
      (eventCountByStartHour.get(hourKey) || 0) + 1
    );
  }

  const hours = [];
  for (let h = startHour; h < endHourExclusive; h++) {
    hours.push(h);
  }

  const emptySlots = [];
  for (let h = startHour; h < endHourExclusive; h++) {
    const slotStart = new Date(dayDate);
    slotStart.setHours(h, 0, 0, 0);
    const slotEnd = new Date(dayDate);
    slotEnd.setHours(h + 1, 0, 0, 0);
    if (!hourSlotHasEvent(geomRows, slotStart, slotEnd)) {
      emptySlots.push({
        key: `empty-slot-${h}-${dayDate.getTime()}`,
        slotStart,
        slotEnd,
      });
    }
  }

  const footerBlackoutRows =
    fullDaySummaries.length > 0 ? fullDaySummaries : partialBlackouts;

  /** Horizontal rules only on hour boundaries where nothing is scheduled (no class/PT/facility; not partial blackout). */
  const hourBoundaryLines = [];
  for (let i = 0; i < numHours; i++) {
    const h = startHour + i;
    const slotStart = new Date(dayDate);
    slotStart.setHours(h, 0, 0, 0);
    const slotEnd = new Date(dayDate);
    slotEnd.setHours(h + 1, 0, 0, 0);
    const occupied =
      hourSlotHasEvent(geomRows, slotStart, slotEnd) ||
      hourSlotHasPartialBlackout(geomPartials, slotStart, slotEnd);
    if (occupied) continue;
    hourBoundaryLines.push(
      <div
        key={`hour-line-${h}`}
        className="scheduling-overview-day-timeline-hour-line"
        style={{
          top: `calc(${((i + 1) / numHours) * 100}% - 1px)`,
        }}
        aria-hidden
      />
    );
  }

  return (
    <div className="scheduling-overview-day-timeline-wrap">
      <div className="scheduling-overview-day-view-summary-line">
        <span>{eventCount} Events Scheduled</span>
        <span className="scheduling-overview-day-view-summary-dot" aria-hidden>
          •
        </span>
        <span>{emptySlots.length} Available Slots</span>
        <span className="scheduling-overview-day-view-summary-dot" aria-hidden>
          •
        </span>
        <span>{blockedCount} Blocked Time</span>
      </div>

      <div
        className="scheduling-overview-day-timeline"
        style={{
          "--scheduling-day-hour-count": String(numHours),
        }}
      >
        <div className="scheduling-overview-day-timeline-hour-grid" aria-hidden>
          {hourBoundaryLines}
        </div>
        <div className="scheduling-overview-day-timeline-gutter" aria-hidden>
          {hours.map((h) => (
            <div key={h} className="scheduling-overview-day-timeline-hour">
              <span className="scheduling-overview-day-timeline-hour-label">
                {formatHourLabel12(h)}
              </span>
            </div>
          ))}
        </div>
        <div className="scheduling-overview-day-timeline-track">
          {emptySlots.map(({ key, slotStart, slotEnd }) => {
            const isOpen = activeSlotKey === key;
            const remainingMin =
              totalMin - (slotStart.getTime() - rangeStartMs) / 60000;
            const flipsUp = remainingMin < 120;
            return (
              <div
                key={key}
                className={
                  "scheduling-overview-day-available-slot-wrap" +
                  (isOpen ? " is-open" : "")
                }
                style={dayTimelinePositionStyle(
                  { start: slotStart, end: slotEnd },
                  rangeStartMs,
                  totalMin
                )}
              >
                <div
                  className={
                    "scheduling-overview-day-slot-anchor" +
                    (flipsUp ? " slot-picker-flips-up" : "")
                  }
                >
                  <button
                    type="button"
                    className="scheduling-overview-day-available-slot"
                    aria-expanded={isOpen}
                    aria-label="Create event in this time slot"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveSlotKey(isOpen ? null : key);
                    }}
                  >
                    <span
                      className="scheduling-overview-day-available-slot-icon"
                      aria-hidden
                    >
                      +
                    </span>
                    <span className="scheduling-overview-day-available-slot-label">
                      Available slot
                    </span>
                  </button>
                  {isOpen && (
                    <ul className="scheduling-overview-day-slot-picker">
                      <li>
                        <button
                          type="button"
                          className="scheduling-overview-create-event-item"
                          onClick={() => {
                            setActiveSlotKey(null);
                            onSlotCreate?.("class", slotStart);
                          }}
                        >
                          <span
                            className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--class"
                            aria-hidden
                          >
                            <img src="/img/new-icons/classes-icon.svg" alt="" />
                          </span>
                          <span>Create Class</span>
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="scheduling-overview-create-event-item"
                          onClick={() => {
                            setActiveSlotKey(null);
                            onSlotCreate?.("pt", slotStart);
                          }}
                        >
                          <span
                            className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--pt"
                            aria-hidden
                          >
                            <img
                              src="/img/new-icons/pt-session-icon.svg"
                              alt=""
                            />
                          </span>
                          <span>Create PT Session</span>
                        </button>
                      </li>
                      {hasNonPullScheduling && (
                        <li>
                          <button
                            type="button"
                            className="scheduling-overview-create-event-item"
                            onClick={() => {
                              setActiveSlotKey(null);
                              onSlotCreate?.("facility", slotStart);
                            }}
                          >
                            <span
                              className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--facility"
                              aria-hidden
                            >
                              <img
                                src="/img/new-icons/facilities-icon.svg"
                                alt=""
                              />
                            </span>
                            <span>Create Facility</span>
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}

          {timelineRows.map((row) => {
            const g = geomByKey.get(row.key) || row;
            const hourKey =
              g.start instanceof Date && !Number.isNaN(g.start.getTime())
                ? `${startOfLocalDay(g.start).getTime()}-${g.start.getHours()}`
                : "";
            const durationMin =
              g.start instanceof Date && g.end instanceof Date
                ? (g.end.getTime() - g.start.getTime()) / 60000
                : null;
            const compactInHour =
              (eventCountByStartHour.get(hourKey) || 0) > 1 ||
              (durationMin !== null && durationMin <= 45);
            const col = colMap.get(row.key) ?? 0;
            const totalCols = totalColsMap.get(row.key) ?? 1;
            const overlapStyle =
              totalCols > 1
                ? {
                    left: `calc(${(col / totalCols) * 100}% + ${col > 0 ? 1 : 0}px)`,
                    width: `calc(${(1 / totalCols) * 100}% - ${col > 0 ? 1 : 0}px)`,
                    right: "auto",
                    zIndex: 2 + col,
                    ...(col > 0 ? { paddingLeft: "0.15rem" } : {}),
                    ...(col < totalCols - 1 ? { paddingRight: "0.15rem" } : {}),
                  }
                : {};
            return (
              <div
                key={row.key}
                className={[
                  "scheduling-overview-day-timeline-event",
                  row.kind &&
                    `scheduling-overview-day-timeline-event--${row.kind}`,
                  compactInHour &&
                    "scheduling-overview-day-timeline-event--hour-compact",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  ...dayTimelinePositionStyle(g, rangeStartMs, totalMin),
                  ...overlapStyle,
                }}
              >
                <SchedulingOverviewStackedRow
                  row={row}
                  activeUser={activeUser}
                  theme={theme}
                  coachAvatarSrc={coachAvatarSrc}
                  previewFocusKey={previewFocusKey}
                  layoutVariant="day"
                  inColumnLayout={totalCols > 1}
                  showTime={totalCols === 1 || col === 0}
                  slotDurationMin={durationMin}
                  onEventClick={onEventClick}
                  onEventEdit={onEventEdit}
                  tz={tz}
                />
              </div>
            );
          })}
        </div>
      </div>

      {footerBlackoutRows.length > 0 ? (
        <div className="scheduling-overview-day-blackout-footer">
          {footerBlackoutRows.map((row) => (
            <MonthBlackoutInfoPopover key={row.key} row={row} tz={tz} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Single row in week/day stacked lists: blackout summary card or class/PT/facility event.
 */
function SchedulingOverviewStackedRow(props) {
  if (props.row.kind === "blackout_summary") {
    const { row, tz } = props;
    const o = row.blackoutOverride;
    const tip =
      row.fullDayTooltip ||
      [o?.name, o?.reason ?? o?.description].filter(Boolean).join(" — ") ||
      undefined;
    return (
      <div className="scheduling-overview-blackout-summary" title={tip}>
        <strong className="scheduling-overview-blackout-summary-title">
          Blacked out
        </strong>
        <span className="scheduling-overview-blackout-summary-date">
          {formatBlackoutSummaryHeadingDate(row.start)}
        </span>
        {!row.fullDayOnly && (
          <span className="scheduling-overview-blackout-summary-time">
            {formatBlackoutSummaryTimeRange(row, tz)}
          </span>
        )}
      </div>
    );
  }
  return <SchedulingOverviewEventRow {...props} />;
}

function SchedulingOverviewEventRow({
  row,
  activeUser,
  theme,
  coachAvatarSrc,
  previewFocusKey,
  layoutVariant = "week",
  inColumnLayout = false,
  showTime = true,
  slotDurationMin = null,
  onEventClick,
  onEventEdit,
  tz,
}) {
  const { locals: rowLocals } = useRillContext();
  const organizationRoles = Array.isArray(rowLocals?.organizationRoles)
    ? rowLocals.organizationRoles
    : [];
  const handleEventActivate = (e) => {
    e?.stopPropagation?.();
    if (typeof onEventClick === "function") {
      onEventClick(row);
    }
  };
  const eventClickProps = {
    role: "button",
    tabIndex: 0,
    onClick: handleEventActivate,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleEventActivate(e);
      }
    },
  };
  const canEditRow = canManageOverviewEvent(row, activeUser);
  const handleEventEdit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (typeof onEventEdit === "function" && row.kind !== "blackout_summary") {
      onEventEdit(row);
    }
  };
  const ev = row;
  const classCoachAvatars =
    ev.kind === "class" && ev.raw
      ? resolveClassCoachAvatarList(ev.raw, activeUser, theme)
      : [];
  const ptCoachAvatars =
    ev.kind === "pt" && ev.raw
      ? resolveClassCoachAvatarList(ev.raw, activeUser, theme)
      : [coachAvatarSrc];
  const ptRawCoachCount =
    ev.kind === "pt"
      ? Array.isArray(ev.raw?.coaches)
        ? ev.raw.coaches.length
        : 0
      : 0;
  const ptIsMultiCoach = ptCoachAvatars.length > 1 || ptRawCoachCount > 1;
  const ptSubtitle = null;
  const coachDetailList = React.useMemo(() => {
    if (ev.kind !== "class" && ev.kind !== "pt") return [];
    if (!Array.isArray(ev.raw?.coaches)) return [];
    const seen = new Set();
    const out = [];
    for (const entry of ev.raw.coaches) {
      const c = entry?.coachId ?? entry?.coach ?? entry;
      const obj = c && typeof c === "object" ? c : {};
      const id = String(obj._id ?? obj.id ?? obj.email ?? obj.name ?? "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const fallbackName =
        String(obj.name ?? "").trim() ||
        [obj.firstName, obj.lastName].filter(Boolean).join(" ").trim() ||
        "Coach";
      const orgRoleName = obj.organizationRoleId
        ? getRoleNameById(organizationRoles, String(obj.organizationRoleId))
        : "";
      const role = orgRoleName || "Coach";
      out.push({
        id,
        name: fallbackName,
        role,
        avatarUrl: getAvatarUrl(obj, false, theme, theme !== "dark"),
      });
    }
    return out;
  }, [ev.kind, ev.raw, theme, organizationRoles]);
  const bookedMembersDeduped = React.useMemo(() => {
    const list = Array.isArray(ev.raw?.booked_members)
      ? ev.raw.booked_members
      : [];
    const seen = new Set();
    const out = [];
    for (const m of list) {
      const k = String(m.dedupeKey ?? m.email ?? m.id ?? m.name ?? "");
      if (!k || seen.has(k)) continue;
      seen.add(k);
      out.push(m);
    }
    return out;
  }, [ev.raw?.booked_members]);
  const bookedMembers = bookedMembersDeduped;
  const ptCapacity = ev.kind === "pt" ? getPtCapacitySummary(ev.raw) : null;
  const [coachPopoverPos, setCoachPopoverPos] = React.useState(null);
  const [clientPopoverPos, setClientPopoverPos] = React.useState(null);
  const coachPopoverOpen = !!coachPopoverPos;
  const clientPopoverOpen = !!clientPopoverPos;
  const handleCoachHoverEnter = React.useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setCoachPopoverPos({ left: r.left, top: r.bottom + 6 });
  }, []);
  const handleClientHoverEnter = React.useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setClientPopoverPos({ left: r.left, top: r.bottom + 6 });
  }, []);
  const previewHighlight =
    previewFocusKey != null && ev.key === previewFocusKey
      ? " scheduling-overview-event--preview-focus"
      : "";
  const blackoutMod =
    !ev.singleEventBlock && (ev.blackoutOverride || ev.coachBlackout)
      ? " scheduling-overview-event--blackout-affected"
      : "";

  const singleEventMod = ev.singleEventBlock
    ? " scheduling-overview-event--single-event-block"
    : "";
  const enrolledCount =
    typeof ev.raw?.enrolled_count === "number" ? ev.raw.enrolled_count : 0;
  const isFree =
    !ev.singleEventBlock &&
    !ev.blackoutOverride &&
    !ev.coachBlackout &&
    enrolledCount === 0 &&
    !(ptCapacity?.isFull ?? false) &&
    ev.start instanceof Date &&
    ev.start.getTime() > Date.now();

  if (layoutVariant === "day") {
    const dayCoachAvatar =
      ev.kind === "class"
        ? (classCoachAvatars[0] ?? null)
        : ev.kind === "pt"
          ? (ptCoachAvatars[0] ?? coachAvatarSrc)
          : coachAvatarSrc;
    const dayCoachName =
      ev.kind === "class" && ev.raw
        ? resolveClassCoachLabel(ev.raw, activeUser)
        : ev.kind === "pt" && ev.raw
          ? resolvePtCoachLabel(ev.raw, activeUser)
          : "";
    if (ev.kind === "pt" || ev.kind === "facility") {
      return (
        <article
          data-scheduling-preview-key={ev.key}
          className={
            "scheduling-overview-event scheduling-overview-event--stacked scheduling-overview-event--day-card scheduling-overview-event--day-card-pt-inline" +
            overviewStackedEventClass(ev.kind) +
            previewHighlight +
            blackoutMod +
            singleEventMod
          }
          {...(eventClickProps || {})}
        >
          {showTime ? (
            <time className="scheduling-overview-event-time">
              {formatAmPm(ev.start, ev.raw?.timezone)} -{" "}
              {formatAmPm(ev.end, ev.raw?.timezone)}
            </time>
          ) : null}
          {dayCoachAvatar ? (
            <img
              src={dayCoachAvatar}
              alt=""
              className={
                "scheduling-overview-day-card-pt-avatar" +
                (slotDurationMin !== null && slotDurationMin <= 15
                  ? " scheduling-overview-day-card-pt-avatar--tiny"
                  : "")
              }
            />
          ) : null}
          <h3 className="scheduling-overview-event-title scheduling-overview-day-card-pt-title">
            {ev.title}
          </h3>
          {ptCapacity?.capacity != null ? (
            <span className="scheduling-overview-event-meta">
              {ptCapacity.booked}/{ptCapacity.capacity} booked ·{" "}
              {ptCapacity.remaining} left
            </span>
          ) : null}
          {isFree ? (
            <span className="scheduling-overview-event-badge scheduling-overview-event-badge--free">
              FREE
            </span>
          ) : null}
          {ev.kind === "pt" && ptCapacity?.isFull ? (
            <span className="scheduling-overview-event-badge scheduling-overview-event-badge--full">
              FULL
            </span>
          ) : null}
          {canEditRow ? (
            <button
              type="button"
              className="scheduling-overview-day-card-edit"
              onClick={handleEventEdit}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Edit ${ev.title}`}
            >
              <span className="scheduling-overview-day-card-edit-icon" />
              {!inColumnLayout && "Edit"}
            </button>
          ) : null}
        </article>
      );
    }

    if (ev.kind === "class" && inColumnLayout) {
      return (
        <article
          data-scheduling-preview-key={ev.key}
          className={
            "scheduling-overview-event scheduling-overview-event--stacked scheduling-overview-event--day-card scheduling-overview-event--day-card-class-inline" +
            overviewStackedEventClass(ev.kind) +
            previewHighlight +
            blackoutMod +
            singleEventMod
          }
          {...(eventClickProps || {})}
        >
          <h3 className="scheduling-overview-event-title scheduling-overview-day-card-pt-title">
            {ev.title}
          </h3>
          {ev.subtitle ? (
            <span className="scheduling-overview-event-meta scheduling-overview-day-card-class-slots">
              {ev.subtitle}
            </span>
          ) : null}
          {canEditRow ? (
            <button
              type="button"
              className="scheduling-overview-day-card-edit"
              onClick={handleEventEdit}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Edit ${ev.title}`}
            >
              <span className="scheduling-overview-day-card-edit-icon" />
            </button>
          ) : null}
        </article>
      );
    }

    return (
      <article
        data-scheduling-preview-key={ev.key}
        className={
          "scheduling-overview-event scheduling-overview-event--stacked scheduling-overview-event--day-card" +
          overviewStackedEventClass(ev.kind) +
          previewHighlight +
          blackoutMod +
          singleEventMod
        }
        {...(eventClickProps || {})}
      >
        <div className="scheduling-overview-day-card-main">
          <div className="scheduling-overview-day-card-title-row">
            <time className="scheduling-overview-event-time">
              {formatAmPm(ev.start, ev.raw?.timezone)} -{" "}
              {formatAmPm(ev.end, ev.raw?.timezone)}
            </time>
          </div>
          <h3 className="scheduling-overview-event-title">{ev.title}</h3>
          <div className="scheduling-overview-day-card-meta-row">
            {ev.kind === "class" && ev.subtitle ? (
              <p className="scheduling-overview-event-meta">{ev.subtitle}</p>
            ) : null}
            {ev.singleEventBlock ? (
              <span className="scheduling-overview-event-badge scheduling-overview-event-badge--blocked">
                Blocked
              </span>
            ) : null}
            {ev.kind === "class" && ev.bookingFull ? (
              <span className="scheduling-overview-event-badge scheduling-overview-event-badge--full">
                Full
              </span>
            ) : null}
            {isFree ? (
              <span className="scheduling-overview-event-badge scheduling-overview-event-badge--free">
                FREE
              </span>
            ) : null}
          </div>
        </div>

        <div className="scheduling-overview-day-card-side">
          {dayCoachName ? (
            <div className="scheduling-overview-event-coach">
              {dayCoachAvatar ? <img src={dayCoachAvatar} alt="" /> : null}
              <span>{dayCoachName}</span>
            </div>
          ) : null}
          {canEditRow ? (
            <button
              type="button"
              className="scheduling-overview-day-card-edit"
              onClick={handleEventEdit}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Edit ${ev.title}`}
            >
              <span className="scheduling-overview-day-card-edit-icon" />
              Edit
            </button>
          ) : null}
        </div>
      </article>
    );
  }

  if (
    layoutVariant === "day" &&
    (ev.blackoutOverride || ev.coachBlackout) &&
    !ev.singleEventBlock
  ) {
    return (
      <article
        data-scheduling-preview-key={ev.key}
        className={
          "scheduling-overview-event scheduling-overview-event--stacked scheduling-overview-event--day-blackout-compact" +
          previewHighlight
        }
      >
        <h3 className="scheduling-overview-event-title scheduling-overview-event-title--day-blackout">
          {ev.title}
        </h3>
      </article>
    );
  }

  if (ev.singleEventBlock) {
    return (
      <article
        data-scheduling-preview-key={ev.key}
        className={
          "scheduling-overview-event scheduling-overview-event--stacked" +
          overviewStackedEventClass(ev.kind) +
          previewHighlight +
          singleEventMod
        }
        {...(eventClickProps || {})}
      >
        <div className="scheduling-overview-event-head">
          <time className="scheduling-overview-event-time">
            {formatAmPm(ev.start, ev.raw?.timezone)} –{" "}
            {formatAmPm(ev.end, ev.raw?.timezone)}
          </time>
          <span className="scheduling-overview-event-badge scheduling-overview-event-badge--blocked">
            Blocked
          </span>
        </div>
        <h3 className="scheduling-overview-event-title">{ev.title}</h3>
        {ev.kind === "class" &&
          (ev.subtitle || classCoachAvatars.length > 0) && (
            <div className="scheduling-overview-event-class-row">
              {ev.subtitle ? (
                <p className="scheduling-overview-event-meta">{ev.subtitle}</p>
              ) : (
                <span className="scheduling-overview-event-class-row-spacer" />
              )}
              {classCoachAvatars.length > 0 && (
                <div className="scheduling-overview-event-coach-stack">
                  {classCoachAvatars.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt=""
                      className="scheduling-overview-event-coach-stack-avatar"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        {ev.kind === "pt" &&
          (ptIsMultiCoach ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%",
                minWidth: 0,
              }}
            >
              {ptSubtitle ? (
                <p
                  className="scheduling-overview-event-meta"
                  style={{ flex: 1, margin: 0 }}
                >
                  {ptSubtitle}
                </p>
              ) : null}
              <div className="scheduling-overview-event-coach-stack">
                {ptCoachAvatars.map((url, i) => (
                  <img
                    key={i}
                    src={url}
                    alt=""
                    className="scheduling-overview-event-coach-stack-avatar"
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="scheduling-overview-event-coach">
              <img src={ptCoachAvatars[0] || coachAvatarSrc} alt="" />
              <span>{resolvePtCoachLabel(ev.raw, activeUser)}</span>
            </div>
          ))}
      </article>
    );
  }

  return (
    <article
      data-scheduling-preview-key={ev.key}
      className={
        "scheduling-overview-event scheduling-overview-event--stacked" +
        overviewStackedEventClass(ev.kind) +
        previewHighlight +
        blackoutMod
      }
      {...(eventClickProps || {})}
    >
      <div className="scheduling-overview-event-head">
        <time className="scheduling-overview-event-time">
          {formatAmPm(ev.start, ev.raw?.timezone)} –{" "}
          {formatAmPm(ev.end, ev.raw?.timezone)}
        </time>
      </div>
      <h3 className="scheduling-overview-event-title">{ev.title}</h3>
      {ev.kind === "pt" && ptCapacity?.capacity != null && (
        <p className="scheduling-overview-event-meta">
          {ptCapacity.booked}/{ptCapacity.capacity} booked ·{" "}
          {ptCapacity.remaining} left
        </p>
      )}
      {ev.kind === "class" && ev.subtitle && (
        <p className="scheduling-overview-event-meta">{ev.subtitle}</p>
      )}
      {ev.kind === "pt" && bookedMembers.length > 0 && (
        <div
          className="scheduling-overview-event-client"
          onMouseEnter={handleClientHoverEnter}
          onMouseLeave={() => setClientPopoverPos(null)}
        >
          <img
            className="scheduling-overview-event-client-avatar"
            src={bookedMembers[0].avatarUrl}
            alt=""
          />
          <span className="scheduling-overview-event-client-name">
            {bookedMembers[0].name}
            {bookedMembers.length > 1 ? ` +${bookedMembers.length - 1}` : ""}
          </span>
          {clientPopoverOpen && (
            <div
              className="scheduling-overview-popover scheduling-overview-popover--client"
              role="tooltip"
              style={{
                position: "fixed",
                left: clientPopoverPos.left,
                top: clientPopoverPos.top,
              }}
            >
              <div className="scheduling-overview-popover-section">
                <span className="scheduling-overview-popover-label">
                  BOOKED BY
                </span>
                {bookedMembers.map((m) => (
                  <div key={m.id} className="scheduling-overview-popover-row">
                    <img src={m.avatarUrl} alt="" />
                    <div className="scheduling-overview-popover-text">
                      <div className="scheduling-overview-popover-name">
                        {m.name}
                      </div>
                      {m.email && (
                        <div className="scheduling-overview-popover-sub">
                          {m.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {coachDetailList.length > 0 && (
                <div className="scheduling-overview-popover-section">
                  <span className="scheduling-overview-popover-label">
                    COACH
                  </span>
                  {coachDetailList.map((c) => (
                    <div key={c.id} className="scheduling-overview-popover-row">
                      <img src={c.avatarUrl} alt="" />
                      <div className="scheduling-overview-popover-text">
                        <div className="scheduling-overview-popover-name">
                          {c.name}
                        </div>
                        {c.role && (
                          <div className="scheduling-overview-popover-sub">
                            {c.role}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {(ev.kind === "pt" || ev.kind === "class" || ev.kind === "facility") && (
        <div
          className="scheduling-overview-event-coach-stack scheduling-overview-event-coach-stack--corner"
          onMouseEnter={handleCoachHoverEnter}
          onMouseLeave={() => setCoachPopoverPos(null)}
        >
          {ev.kind === "class" && ev.bookingFull && (
            <span className="scheduling-overview-event-badge scheduling-overview-event-badge--full">
              Full
            </span>
          )}
          {ev.kind === "pt" && ptCapacity?.isFull && (
            <span className="scheduling-overview-event-badge scheduling-overview-event-badge--full">
              Full
            </span>
          )}
          {isFree && (
            <span className="scheduling-overview-event-badge scheduling-overview-event-badge--free">
              FREE
            </span>
          )}
          {(ev.kind === "class" ? classCoachAvatars : ptCoachAvatars).map(
            (url, i) => (
              <img
                key={i}
                src={url}
                alt=""
                className="scheduling-overview-event-coach-stack-avatar"
              />
            )
          )}
          {coachPopoverOpen && coachDetailList.length > 0 && (
            <div
              className="scheduling-overview-popover scheduling-overview-popover--coach"
              role="tooltip"
              style={{
                position: "fixed",
                left: coachPopoverPos.left,
                top: coachPopoverPos.top,
              }}
            >
              <div className="scheduling-overview-popover-section">
                <span className="scheduling-overview-popover-label">COACH</span>
                {coachDetailList.map((c) => (
                  <div key={c.id} className="scheduling-overview-popover-row">
                    <img src={c.avatarUrl} alt="" />
                    <div className="scheduling-overview-popover-text">
                      <div className="scheduling-overview-popover-name">
                        {c.name}
                      </div>
                      {c.role && (
                        <div className="scheduling-overview-popover-sub">
                          {c.role}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Monday-start month grid: only full weeks that touch `anchor`’s month
 * (leading/trailing days from adjacent months only to pad those weeks — no extra week beyond).
 */
function buildMonthGrid(anchorDate) {
  const y = anchorDate.getFullYear();
  const m = anchorDate.getMonth();
  const firstOfMonth = new Date(y, m, 1, 0, 0, 0, 0);
  const lastOfMonth = new Date(y, m + 1, 0, 0, 0, 0, 0);
  const gridStart = startOfWeekMonday(firstOfMonth);
  const daysToSunday = 6 - mondayIndexFromDate(lastOfMonth);
  const gridEnd = addDays(lastOfMonth, daysToSunday);
  gridEnd.setHours(0, 0, 0, 0);
  const cells = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
    cells.push({ date: new Date(d), inMonth: d.getMonth() === m });
  }
  return cells;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MONTH_PILL_MAX = 4;

const SchedulingOverviewCalendar = ({
  preview: previewProp,
  previewId: previewIdProp,
  hasNonPullScheduling = false,
  hasPressScheduling = false,
  hasPullScheduling = false,
  canPushUser = true,
  canPullUser = true,
}) => {
  const { locals } = useRillContext();
  const activeUser = locals?.activeUser ?? {};
  const theme = locals.activeUser.webAppTheme || "dark";
  const fetchedTz = useGymTimezone();
  const gymTimezone = fetchedTz || null;
  const coachAvatarSrc = getAvatarUrl(
    activeUser,
    false,
    theme,
    theme !== "dark"
  );

  const [view, setView] = React.useState("week");
  const [weekAnchor, setWeekAnchor] = React.useState(() => new Date());
  const [classes, setClasses] = React.useState([]);
  const [ptSessions, setPtSessions] = React.useState([]);
  const [ptAvailability, setPtAvailability] = React.useState({});
  const [facilities, setFacilities] = React.useState([]);
  const [facilityAvailability, setFacilityAvailability] = React.useState({});
  const [overrides, setOverrides] = React.useState([]);
  const [coachBlackouts, setCoachBlackouts] = React.useState([]);
  const [bookingCountMap, setBookingCountMap] = React.useState({});
  const [bookingMembersMap, setBookingMembersMap] = React.useState({});
  const [ptBookingEventsBySessionId, setPtBookingEventsBySessionId] =
    React.useState({});
  const [
    facilityBookingEventsByFacilityId,
    setFacilityBookingEventsByFacilityId,
  ] = React.useState({});
  const [classBookingEventsByClassId, setClassBookingEventsByClassId] =
    React.useState({});
  const [orphanPtSessionsById, setOrphanPtSessionsById] = React.useState({});
  const [orphanFacilitiesById, setOrphanFacilitiesById] = React.useState({});
  const [orphanClassesById, setOrphanClassesById] = React.useState({});
  const [orgCoaches, setOrgCoaches] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/actions/scheduling/coaches", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        setOrgCoaches(Array.isArray(body?.coaches) ? body.coaches : []);
      })
      .catch(() => {
        if (!cancelled) setOrgCoaches([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [statusMenuOpen, setStatusMenuOpen] = React.useState(false);
  const [filtersSlotEl, setFiltersSlotEl] = React.useState(null);
  const [filterKinds, setFilterKinds] = React.useState(new Set());
  const [filterCoachIds, setFilterCoachIds] = React.useState(new Set());
  const [filterEventIds, setFilterEventIds] = React.useState(new Set());
  const [filterStatuses, setFilterStatuses] = React.useState(new Set(["all"]));
  const [coachDefaultSeeded, setCoachDefaultSeeded] = React.useState(false);
  const filterBlocked = filterStatuses.has("blockedTime");
  const selectStatus = React.useCallback((status) => {
    setFilterStatuses(new Set([status]));
    setStatusMenuOpen(false);
  }, []);

  const applyOverviewKindTab = React.useCallback(
    (kind) => {
      const turningOff =
        kind != null && filterKinds.size === 1 && filterKinds.has(kind);
      const nextKind = turningOff || kind == null ? null : kind;
      setFilterKinds(nextKind ? new Set([nextKind]) : new Set());
      setFilterStatuses(
        nextKind === "pt" || nextKind === "facility"
          ? new Set(["booked"])
          : new Set(["all"])
      );
    },
    [filterKinds]
  );

  const [expandedMonthDay, setExpandedMonthDay] = React.useState(null);

  const [createEventMenuOpen, setCreateEventMenuOpen] = React.useState(false);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [blockTimeModalOpen, setBlockTimeModalOpen] = React.useState(false);
  const [blockTimePreset, setBlockTimePreset] = React.useState(null);
  const [blockDetailsModalEvent, setBlockDetailsModalEvent] =
    React.useState(null);
  const [markAttendanceEvent, setMarkAttendanceEvent] = React.useState(null);
  const [bookingsForPanel, setBookingsForPanel] = React.useState([]);
  const [bookingsForPanelLoading, setBookingsForPanelLoading] =
    React.useState(false);
  const [slotModal, setSlotModal] = React.useState(null);

  const handleSlotCreate = React.useCallback((kind, slotStart) => {
    const hh = String(slotStart.getHours()).padStart(2, "0");
    const startTime = `${hh}:00`;
    const endHh = String(Math.min(slotStart.getHours() + 1, 23)).padStart(
      2,
      "0"
    );
    const endTime = `${endHh}:00`;
    const dow = mondayIndexFromDate(slotStart);
    const y = slotStart.getFullYear();
    const mo = String(slotStart.getMonth() + 1).padStart(2, "0");
    const d = String(slotStart.getDate()).padStart(2, "0");
    const dateStr = `${y}-${mo}-${d}`;
    const initialRecord =
      kind === "class"
        ? {
            start_time: startTime,
            recurrence_type: "one_time",
            recurrence_start_date: dateStr,
          }
        : {
            windows: [
              { day_of_week: dow, start_time: startTime, end_time: endTime },
            ],
          };
    setSlotModal({ kind, initialRecord });
  }, []);

  const handleEventClick = React.useCallback((row) => {
    if (!row) return;
    const normalized =
      row.kind === "blackout_summary" && !row.raw
        ? {
            ...row,
            raw: row.blackoutOverride ?? {},
            title:
              row.blackoutOverride?.name ||
              row.fullDayTooltip ||
              "Blocked Time",
          }
        : row;
    setSelectedEvent(normalized);
  }, []);

  const closeEventPanel = React.useCallback(() => {
    setSelectedEvent(null);
  }, []);

  React.useEffect(() => {
    if (!selectedEvent) return;
    const handlePointerDown = (e) => {
      if (e.target.closest(".event-details-panel")) return;
      closeEventPanel();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [selectedEvent, closeEventPanel]);

  const statusMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const handlePointerDown = (e) => {
      if (statusMenuRef.current?.contains(e.target)) return;
      setStatusMenuOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [statusMenuOpen]);

  React.useEffect(() => {
    setFiltersSlotEl(
      document.getElementById("scheduling-overview-filters-slot")
    );
  }, []);

  const handleEditEvent = React.useCallback(
    (event) => {
      if (!event?.raw) return;
      if (!canManageOverviewEvent(event, activeUser)) return;
      const kind = event.kind;
      if (kind !== "class" && kind !== "pt" && kind !== "facility") return;
      let initialRecord = event.raw;
      const rawId = String(initialRecord._id ?? initialRecord.id ?? "");
      if (rawId && !Array.isArray(initialRecord.windows)) {
        const av =
          kind === "pt"
            ? ptAvailability[rawId]
            : kind === "facility"
              ? facilityAvailability[rawId]
              : null;
        if (av) {
          const wins = windowsFromAvailabilityPayload(av);
          if (wins.length > 0)
            initialRecord = { ...initialRecord, windows: wins };
        }
      }
      setSlotModal({ kind, initialRecord });
    },
    [ptAvailability, facilityAvailability, activeUser]
  );

  const handleMarkAttendance = React.useCallback(
    (event) => {
      if (!canManageOverviewEvent(event, activeUser)) return;
      const rawId = event.raw?._id ?? event.raw?.id;
      setMarkAttendanceEvent({
        ...event,
        rawId: rawId ? String(rawId) : undefined,
        type: event.kind,
      });
      setSelectedEvent(null);
    },
    [activeUser]
  );

  const handleBlockTimeFromPanel = React.useCallback(
    (event) => {
      if (!event?.raw) return;
      if (!canManageOverviewEvent(event, activeUser)) return;
      const id = event.raw._id ?? event.raw.id;
      if (!id) return;
      setSelectedEvent(null);
      setBlockDetailsModalEvent(event);
    },
    [activeUser]
  );

  const handleBlockThisEvent = React.useCallback(
    async (event) => {
      if (!event?.raw || !event.start) return;
      if (!canManageOverviewEvent(event, activeUser)) return;
      const id = String(event.raw._id ?? event.raw.id ?? "");
      if (!id) return;
      setSelectedEvent(null);
      try {
        const payloads = buildBlockTimeBlackoutPayloads({
          bookableIds: [id],
          dateMode: BLOCK_DATE_SINGLE,
          singleDayDate: event.start,
        });
        await submitBlockTimePayloads(payloads);
      } finally {
        loadData();
      }
    },
    [activeUser]
  );

  const handleUnblockEvent = React.useCallback(
    async (event) => {
      if (!canManageOverviewEvent(event, activeUser)) return;
      // The blackout id lives on the override (single-event block / override
      // summary) or on the coach blackout doc — not on the bookable's raw doc.
      const ov = event?.blackoutOverride ?? event?.raw ?? {};
      const blackoutId =
        ov.blackoutId ??
        ov.blackout_id ??
        ov._id ??
        ov.id ??
        event?.raw?.blackoutId ??
        event?.raw?.blackout_id;
      if (!blackoutId) return;
      setSelectedEvent(null);
      try {
        await fetch(
          `/actions/scheduling/blackouts/${encodeURIComponent(String(blackoutId))}`,
          { method: "DELETE", credentials: "same-origin" }
        );
      } finally {
        loadData();
      }
    },
    [activeUser]
  );

  React.useEffect(() => {
    if (!selectedEvent?.raw) {
      setBookingsForPanel([]);
      return;
    }
    const eventId = String(selectedEvent.raw._id ?? selectedEvent.raw.id ?? "");
    if (!eventId) return;
    let cancelled = false;
    setBookingsForPanelLoading(true);
    const params = new URLSearchParams({
      bookable_event_id: eventId,
      include: "member_type",
    });
    if (
      selectedEvent.start instanceof Date &&
      !Number.isNaN(selectedEvent.start.getTime())
    ) {
      params.set("date", selectedEvent.start.toISOString().slice(0, 10));
    }
    fetch(`/actions/scheduling/coach/bookings?${params}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (!res.ok) return [];
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
      })
      .then((list) => {
        if (cancelled) return;
        const activeList = list.filter((b) => {
          const status = String(b?.status ?? "").toLowerCase();
          if (status === "cancelled" || status === "canceled") return false;
          if (selectedEvent.kind !== "pt") return true;
          return ptBookingMatchesOccurrence(
            b,
            { start: selectedEvent.start, end: selectedEvent.end },
            eventId
          );
        });
        const members = activeList.map((b) => {
          const rawAvatar = b.member_avatar;
          const hasAvatarPath =
            rawAvatar &&
            typeof rawAvatar === "object" &&
            typeof rawAvatar.s3Path === "string" &&
            rawAvatar.s3Path.length > 0;
          const avatarUrl = hasAvatarPath
            ? getAvatarUrl(
                { avatar: rawAvatar },
                false,
                theme,
                theme !== "dark"
              )
            : typeof rawAvatar === "string" && rawAvatar
              ? rawAvatar
              : getAvatarUrl({}, false, theme, theme !== "dark");
          const rawDate = b.start_datetime ?? null;
          const bookedAt = formatBookingSlotLabel(
            rawDate,
            b.event_timezone || gymTimezone
          );
          return {
            id: String(b._id ?? b.id ?? Math.random()),
            name: String(b.member_name ?? "Member"),
            email: b.member_email ? String(b.member_email) : null,
            bookType: b.member_is_push ? "PUSH User" : "PULL User",
            bookedAt,
            statusLabel: b.status ? String(b.status) : null,
            avatarUrl,
          };
        });
        setBookingsForPanel(members);
      })
      .catch(() => {
        if (cancelled) return;
        setBookingsForPanel([]);
      })
      .finally(() => {
        if (cancelled) return;
        setBookingsForPanelLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEvent?.raw, selectedEvent?.start, theme, gymTimezone]);
  const createEventMenuRef = React.useRef(null);

  React.useEffect(() => {
    if (!createEventMenuOpen) return undefined;
    const onDoc = (e) => {
      if (createEventMenuRef.current?.contains(e.target)) return;
      setCreateEventMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [createEventMenuOpen]);

  const bootstrapPreview = React.useMemo(() => {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const p = sp.get("preview");
      const id = sp.get("previewId");
      if (p && id) return { preview: p, previewId: id };
    }
    if (previewProp && previewIdProp) {
      return { preview: previewProp, previewId: String(previewIdProp) };
    }
    return { preview: null, previewId: null };
  }, [previewProp, previewIdProp]);

  const [previewConsumed, setPreviewConsumed] = React.useState(false);
  const [previewFocusKey, setPreviewFocusKey] = React.useState(null);
  const [previewNotice, setPreviewNotice] = React.useState(null);

  const previewKind = previewConsumed
    ? null
    : normalizePreviewKind(bootstrapPreview.preview);
  const previewTargetId = previewConsumed ? null : bootstrapPreview.previewId;

  const weekStart = React.useMemo(
    () => startOfWeekMonday(weekAnchor),
    [weekAnchor]
  );
  const weekEnd = React.useMemo(() => {
    const x = new Date(weekStart);
    x.setDate(x.getDate() + 6);
    x.setHours(23, 59, 59, 999);
    return x;
  }, [weekStart]);

  /** Expand classes/PT over the window needed for the active view (week / month grid / single day). */
  const eventExpandRange = React.useMemo(() => {
    if (view === "day") {
      const a = new Date(weekAnchor);
      a.setHours(0, 0, 0, 0);
      const end = new Date(a);
      end.setHours(23, 59, 59, 999);
      return { start: a, end };
    }
    if (view === "month") {
      const cells = buildMonthGrid(weekAnchor);
      const start = new Date(cells[0].date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(cells[cells.length - 1].date);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    return { start: weekStart, end: weekEnd };
  }, [view, weekAnchor, weekStart, weekEnd]);

  const loadDataAbortRef = React.useRef(null);
  const loadData = React.useCallback(async () => {
    // Abort any in-flight fetch for the previous range — rapid week / month
    // navigation otherwise leaves orphan responses that can overwrite newer
    // state when they resolve out of order.
    loadDataAbortRef.current?.abort();
    const controller = new AbortController();
    loadDataAbortRef.current = controller;
    const fetchOpts = { credentials: "same-origin", signal: controller.signal };
    setError(null);
    setLoading(true);
    try {
      const [rc, rpt, rf, ro, rb] = await Promise.all([
        fetch("/actions/scheduling/classes", fetchOpts),
        fetch("/actions/scheduling/pt-sessions", fetchOpts),
        fetch("/actions/scheduling/facilities", fetchOpts),
        fetch("/actions/scheduling/overrides", fetchOpts),
        fetch("/actions/scheduling/blackouts?scope=org", fetchOpts),
      ]);
      const jc = await rc.json().catch(() => null);
      const jpt = await rpt.json().catch(() => null);
      const jf = await rf.json().catch(() => null);
      const jo = await ro.json().catch(() => null);
      const jb = await rb.json().catch(() => null);
      /** Same shapes as scheduling action + possible direct API wrappers */
      const classList = (() => {
        if (!rc.ok || jc == null) return [];
        if (Array.isArray(jc)) return jc;
        if (Array.isArray(jc.data)) return jc.data;
        if (Array.isArray(jc.classes)) return jc.classes;
        if (Array.isArray(jc.items)) return jc.items;
        if (Array.isArray(jc.results)) return jc.results;
        return [];
      })();
      const ptList = (() => {
        if (!rpt.ok || jpt == null) return [];
        if (Array.isArray(jpt)) return jpt;
        if (Array.isArray(jpt.data)) return jpt.data;
        if (Array.isArray(jpt.sessions)) return jpt.sessions;
        if (Array.isArray(jpt.items)) return jpt.items;
        return [];
      })();
      const facilityList = (() => {
        if (!rf.ok || jf == null) return [];
        if (Array.isArray(jf)) return jf;
        if (Array.isArray(jf.data)) return jf.data;
        if (Array.isArray(jf.facilities)) return jf.facilities;
        if (Array.isArray(jf.items)) return jf.items;
        return [];
      })();
      setClasses(classList);
      setPtSessions(ptList);
      setFacilities(facilityList);
      const overrideList = (() => {
        if (!ro.ok || jo == null) return [];
        if (Array.isArray(jo)) return jo;
        if (Array.isArray(jo.data)) return jo.data;
        if (Array.isArray(jo.results)) return jo.results;
        if (Array.isArray(jo.overrides)) return jo.overrides;
        if (Array.isArray(jo.items)) return jo.items;
        return [];
      })();
      setOverrides(overrideList);
      const coachBlackoutList = (() => {
        if (!rb.ok || jb == null) return [];
        if (Array.isArray(jb)) return jb;
        if (Array.isArray(jb.data)) return jb.data;
        if (Array.isArray(jb.results)) return jb.results;
        if (Array.isArray(jb.blackouts)) return jb.blackouts;
        if (Array.isArray(jb.items)) return jb.items;
        return [];
      })();
      setCoachBlackouts(coachBlackoutList);

      const bkDateKey = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const knownPtIds = new Set(
        ptList.map((p) => String(p._id ?? p.id ?? "")).filter(Boolean)
      );
      const knownFacilityIds = new Set(
        facilityList.map((f) => String(f._id ?? f.id ?? "")).filter(Boolean)
      );
      const knownClassIds = new Set(
        classList.map((c) => String(c._id ?? c.id ?? "")).filter(Boolean)
      );

      // Scope the booking fetch to the currently visible range — full coach
      // history per page load was the dominant client memory cost. Pad by one
      // day on each side so bookings that fall on the boundary are not dropped
      // when the server interprets the YYYY-MM-DD bounds as UTC midnight while
      // the calendar grid is anchored on local midnight.
      const isoDay = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
      };
      const padDay = (d, deltaDays) => {
        const x = new Date(d);
        x.setDate(x.getDate() + deltaDays);
        return x;
      };
      const bookingsQuery = new URLSearchParams({
        from: isoDay(padDay(eventExpandRange.start, -1)),
        to: isoDay(padDay(eventExpandRange.end, 1)),
        include: "member_type",
      }).toString();
      const allBookingsResponse = await fetch(
        `/actions/scheduling/coach/bookings?${bookingsQuery}`,
        fetchOpts
      );
      const allBookingsData = allBookingsResponse.ok
        ? await allBookingsResponse.json().catch(() => [])
        : [];
      const allBookings = Array.isArray(allBookingsData) ? allBookingsData : [];

      const countMap = {};
      const membersMap = {};
      const ptBookingEventsMap = {};
      const facilityBookingEventsMap = {};
      const classBookingEventsMap = {};
      const orphanPt = {};
      const orphanFacilities = {};
      const orphanClasses = {};

      for (const b of allBookings) {
        const status = String(b?.status ?? "").toLowerCase();
        if (status === "cancelled" || status === "canceled") continue;
        const id = String(b.bookableEventId ?? b.event_id ?? "");
        if (!id || id === "undefined") continue;

        const rawDate =
          b.start_datetime ??
          b.start_time ??
          b.booking_date ??
          b.event_date ??
          b.scheduled_date ??
          b.occurrence_date ??
          b.date ??
          b.start_date ??
          null;
        const parsed = rawDate ? new Date(rawDate) : null;
        const dk =
          parsed && !Number.isNaN(parsed.getTime())
            ? bkDateKey(parsed)
            : "__nodate__";
        const key = `${id}::${dk}`;
        countMap[key] = (countMap[key] ?? 0) + 1;

        const rawAvatar = b.member_avatar;
        const hasAvatarPath =
          rawAvatar &&
          typeof rawAvatar === "object" &&
          typeof rawAvatar.s3Path === "string" &&
          rawAvatar.s3Path.length > 0;
        const avatarUrl = hasAvatarPath
          ? getAvatarUrl({ avatar: rawAvatar }, false, theme, theme !== "dark")
          : typeof rawAvatar === "string" && rawAvatar
            ? rawAvatar
            : getAvatarUrl({}, false, theme, theme !== "dark");
        const memberKey = String(
          b.member_id ??
            b.memberId ??
            b.user_id ??
            b.userId ??
            b.member_email ??
            b.member_name ??
            b._id ??
            b.id ??
            ""
        );
        const member = {
          id: String(b._id ?? b.id ?? `${key}-${membersMap[key]?.length ?? 0}`),
          dedupeKey: memberKey,
          name: String(b.member_name ?? "Member"),
          email: b.member_email ? String(b.member_email) : null,
          bookType: b.member_is_push ? "PUSH User" : "PULL User",
          avatarUrl,
        };
        if (!membersMap[key]) membersMap[key] = [];
        if (
          !(memberKey && membersMap[key].some((m) => m.dedupeKey === memberKey))
        ) {
          membersMap[key].push(member);
        }

        const bookingRange = getPtBookingRange(b);
        if (!bookingRange?.end) continue;
        const { start: startDt, end: endDt } = bookingRange;
        const bookingId = String(
          b._id ?? b.id ?? `${id}-${startDt.toISOString()}`
        );
        const eventEntry = {
          bookingId,
          start: startDt,
          end: endDt,
          member,
          ptSessionInstanceId: b.ptSessionInstanceId ?? null,
        };

        const type = String(b.event_type ?? "").toLowerCase();
        const isClass =
          type === "class" ||
          knownClassIds.has(id) ||
          Boolean(b.classInstanceId);
        const isPt = !isClass && (type === "pt_session" || knownPtIds.has(id));
        const isFacility =
          !isClass &&
          !isPt &&
          (type === "facility" || knownFacilityIds.has(id));

        const bookingTimezone =
          typeof b.event_timezone === "string" && b.event_timezone.length > 0
            ? b.event_timezone
            : null;

        if (isClass) {
          if (!classBookingEventsMap[id]) classBookingEventsMap[id] = [];
          classBookingEventsMap[id].push(eventEntry);
          if (!knownClassIds.has(id) && !orphanClasses[id]) {
            orphanClasses[id] = {
              _id: id,
              coachId: b.coachId ? String(b.coachId) : null,
              name: String(b.event_name ?? "Class"),
              timezone: bookingTimezone,
              coaches: b.coachId
                ? [{ coachId: { _id: String(b.coachId) } }]
                : [],
            };
          }
        } else if (isPt) {
          if (!ptBookingEventsMap[id]) ptBookingEventsMap[id] = [];
          ptBookingEventsMap[id].push(eventEntry);
          if (!knownPtIds.has(id) && !orphanPt[id]) {
            orphanPt[id] = {
              _id: id,
              coachId: b.coachId ? String(b.coachId) : null,
              name: String(b.event_name ?? "PT Session"),
              timezone: bookingTimezone,
              coaches: b.coachId
                ? [{ coachId: { _id: String(b.coachId) } }]
                : [],
            };
          }
        } else if (isFacility) {
          if (!facilityBookingEventsMap[id]) facilityBookingEventsMap[id] = [];
          facilityBookingEventsMap[id].push(eventEntry);
          if (!knownFacilityIds.has(id) && !orphanFacilities[id]) {
            orphanFacilities[id] = {
              _id: id,
              coachId: b.coachId ? String(b.coachId) : null,
              name: String(b.event_name ?? "Facility"),
              timezone: bookingTimezone,
            };
          }
        }
      }

      setBookingCountMap(countMap);
      setBookingMembersMap(membersMap);
      setPtBookingEventsBySessionId(ptBookingEventsMap);
      setFacilityBookingEventsByFacilityId(facilityBookingEventsMap);
      setClassBookingEventsByClassId(classBookingEventsMap);
      setOrphanPtSessionsById(orphanPt);
      setOrphanFacilitiesById(orphanFacilities);
      setOrphanClassesById(orphanClasses);

      // List endpoints inline `windows`; no per-event fetch needed.
      const av = {};
      for (const s of ptList) {
        const sid = s._id ?? s.id;
        if (!sid) continue;
        if (Array.isArray(s.windows) && s.windows.length > 0) {
          av[sid] = { windows: s.windows };
        }
      }
      setPtAvailability(av);

      const fav = {};
      for (const f of facilityList) {
        const fid = f._id ?? f.id;
        if (!fid) continue;
        if (Array.isArray(f.windows) && f.windows.length > 0) {
          fav[fid] = { windows: f.windows };
        }
      }
      setFacilityAvailability(fav);
    } catch (e) {
      // Aborted fetches (rapid week / month nav) should leave existing state
      // untouched; only real failures clear and surface an error.
      if (e?.name === "AbortError" || controller.signal.aborted) return;
      setError(e?.message ?? "Failed to load schedule");
      setClasses([]);
      setPtSessions([]);
      setFacilities([]);
      setFacilityAvailability({});
      setOverrides([]);
      setCoachBlackouts([]);
      setBookingCountMap({});
      setBookingMembersMap({});
      setPtBookingEventsBySessionId({});
      setFacilityBookingEventsByFacilityId({});
      setClassBookingEventsByClassId({});
      setOrphanPtSessionsById({});
      setOrphanFacilitiesById({});
      setOrphanClassesById({});
    } finally {
      // Only flip the spinner off if this run is still the latest — otherwise
      // a slower older request can race the newer one and leave loading=false
      // before the newer fetch completes.
      if (loadDataAbortRef.current === controller) setLoading(false);
    }
  }, [eventExpandRange.start, eventExpandRange.end]);

  React.useEffect(() => {
    loadData();
    return () => loadDataAbortRef.current?.abort();
  }, [loadData]);

  React.useEffect(() => {
    if (previewConsumed || loading) return;
    if (!previewKind || !previewTargetId) return;

    const { event, reason } = findNextPreviewOccurrence(
      new Date(),
      previewKind,
      previewTargetId,
      classes,
      ptSessions,
      facilities,
      ptAvailability,
      facilityAvailability
    );

    stripSchedulingPreviewParamsFromUrl();
    setPreviewConsumed(true);
    setPreviewNotice(null);

    if (reason === "not_found") {
      setPreviewNotice("This item is not on the schedule.");
      setPreviewFocusKey(null);
      return;
    }
    if (!event || reason === "no_upcoming") {
      setPreviewNotice("No upcoming occurrences in the next two years.");
      setPreviewFocusKey(null);
      setWeekAnchor(new Date());
      setView("week");
      return;
    }

    setWeekAnchor(new Date(event.start));
    setView("day");
    setPreviewFocusKey(event.key);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const esc =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape
            : (s) => String(s).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        const el = document.querySelector(
          `[data-scheduling-preview-key="${esc(event.key)}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    });
  }, [
    previewConsumed,
    loading,
    previewKind,
    previewTargetId,
    classes,
    ptSessions,
    facilities,
    ptAvailability,
    facilityAvailability,
  ]);

  const allEvents = React.useMemo(() => {
    const { start, end } = eventExpandRange;
    const ev = [];
    for (const c of classes) {
      if (c.status === "cancelled" || c.status === "deleted") continue;
      ev.push(...expandClassEvents(c, start, end));
    }
    for (const orphan of Object.values(orphanClassesById)) {
      const cid = String(orphan._id);
      const classBookings = classBookingEventsByClassId[cid] ?? [];
      const inRangeBookings = classBookings.filter(
        (b) =>
          b.start instanceof Date &&
          b.end instanceof Date &&
          b.start.getTime() <= end.getTime() &&
          b.end.getTime() >= start.getTime()
      );
      ev.push(
        ...buildBookingSlotEvents({
          kind: "class",
          record: orphan,
          title: orphan.name,
          bookings: inRangeBookings,
        })
      );
    }
    for (const s of ptSessions) {
      const sid = s._id ?? s.id;
      const payload = ptAvailability[sid];
      if (s.status === "cancelled" || s.status === "deleted") continue;
      const sessionBookings = ptBookingEventsBySessionId[String(sid)] ?? [];
      const inRangeBookings = sessionBookings.filter(
        (b) =>
          b.start instanceof Date &&
          b.end instanceof Date &&
          b.start.getTime() <= end.getTime() &&
          b.end.getTime() >= start.getTime()
      );
      const consumedBookingIds = new Set();
      const windowEvents = expandPtEvents(s, payload, start, end);
      for (const we of windowEvents) {
        const availableBookings = inRangeBookings.filter(
          (booking) => !consumedBookingIds.has(booking.bookingId)
        );
        const segments = buildPtOccurrenceSegments(
          we,
          availableBookings,
          s,
          sid
        );
        for (const segment of segments) {
          ev.push(segment);
          for (const booking of availableBookings) {
            if (
              booking.start.getTime() < segment.end.getTime() &&
              booking.end.getTime() > segment.start.getTime()
            ) {
              consumedBookingIds.add(booking.bookingId);
            }
          }
        }
      }
      ev.push(
        ...buildBookingSlotEvents({
          kind: "pt",
          record: s,
          title: `PT: ${s.name ?? s.title ?? "PT Session"}`,
          bookings: inRangeBookings.filter(
            (booking) => !consumedBookingIds.has(booking.bookingId)
          ),
        })
      );
    }
    for (const orphan of Object.values(orphanPtSessionsById)) {
      const sid = String(orphan._id);
      const sessionBookings = ptBookingEventsBySessionId[sid] ?? [];
      const inRangeBookings = sessionBookings.filter(
        (b) =>
          b.start instanceof Date &&
          b.end instanceof Date &&
          b.start.getTime() <= end.getTime() &&
          b.end.getTime() >= start.getTime()
      );
      ev.push(
        ...buildBookingSlotEvents({
          kind: "pt",
          record: orphan,
          title: `PT: ${orphan.name}`,
          bookings: inRangeBookings,
        })
      );
    }
    for (const f of facilities) {
      const fid = f._id ?? f.id;
      const payload = facilityAvailability[fid];
      if (f.status === "cancelled" || f.status === "deleted") continue;
      const windowEvents = expandFacilityEvents(f, payload, start, end);
      ev.push(...windowEvents);
      const facilityBookings =
        facilityBookingEventsByFacilityId[String(fid)] ?? [];
      const inRangeBookings = facilityBookings.filter(
        (b) =>
          b.start instanceof Date &&
          b.end instanceof Date &&
          b.start.getTime() <= end.getTime() &&
          b.end.getTime() >= start.getTime()
      );
      for (const b of inRangeBookings) {
        const coveredByWindow = windowEvents.some(
          (we) =>
            b.start.getTime() < we.end.getTime() &&
            b.end.getTime() > we.start.getTime()
        );
        if (coveredByWindow) continue;
        ev.push({
          key: `facility-${String(fid)}-booking-${b.bookingId}`,
          kind: "facility",
          title: f.name ?? f.title ?? "Facility",
          start: b.start,
          end: b.end,
          dateKey: tzDateKey(b.start, f.timezone),
          raw: {
            ...f,
            enrolled_count: 1,
            booked_members: [b.member],
          },
          fromBooking: true,
        });
      }
    }
    for (const orphan of Object.values(orphanFacilitiesById)) {
      const fid = String(orphan._id);
      const facilityBookings = facilityBookingEventsByFacilityId[fid] ?? [];
      const inRangeBookings = facilityBookings.filter(
        (b) =>
          b.start instanceof Date &&
          b.end instanceof Date &&
          b.start.getTime() <= end.getTime() &&
          b.end.getTime() >= start.getTime()
      );
      for (const b of inRangeBookings) {
        ev.push({
          key: `facility-${fid}-booking-${b.bookingId}`,
          kind: "facility",
          title: orphan.name,
          start: b.start,
          end: b.end,
          dateKey: tzDateKey(b.start, orphan.timezone),
          raw: {
            ...orphan,
            enrolled_count: 1,
            booked_members: [b.member],
          },
          fromBooking: true,
        });
      }
    }
    return ev.map((e) => {
      if (e.fromBooking) return e;
      if (!e.raw) return e;
      const id = String(e.raw._id ?? e.raw.id ?? "");
      if (!id) return e;
      const evDk =
        e.start instanceof Date
          ? `${e.start.getFullYear()}-${String(e.start.getMonth() + 1).padStart(2, "0")}-${String(e.start.getDate()).padStart(2, "0")}`
          : "__nodate__";
      const bookedCount =
        bookingCountMap[`${id}::${evDk}`] ??
        bookingCountMap[`${id}::__nodate__`] ??
        0;
      const bookedMembers =
        bookingMembersMap[`${id}::${evDk}`] ??
        bookingMembersMap[`${id}::__nodate__`] ??
        [];
      const newRaw = {
        ...e.raw,
        enrolled_count: bookedCount,
        booked_members: bookedMembers,
      };
      if (e.kind === "class" || e.kind === "facility") {
        const { subtitle, bookingFull } = classCapacitySubtitle(newRaw);
        return { ...e, raw: newRaw, subtitle, bookingFull };
      }
      return { ...e, raw: newRaw };
    });
  }, [
    classes,
    classBookingEventsByClassId,
    orphanClassesById,
    ptSessions,
    ptAvailability,
    ptBookingEventsBySessionId,
    orphanPtSessionsById,
    facilities,
    facilityAvailability,
    facilityBookingEventsByFacilityId,
    orphanFacilitiesById,
    eventExpandRange,
    bookingCountMap,
    bookingMembersMap,
  ]);

  const eventsByKind = React.useMemo(() => {
    const map = { class: [], pt: [], facility: [] };
    const seen = { class: new Set(), pt: new Set(), facility: new Set() };
    for (const ev of allEvents) {
      const k = ev.kind;
      if (!map[k]) continue;
      const id = String(ev.raw?._id ?? ev.raw?.id ?? "");
      if (!id || seen[k].has(id)) continue;
      seen[k].add(id);
      map[k].push({ id, title: ev.title || "Untitled" });
    }
    return map;
  }, [allEvents]);

  const loggedInCoachId = activeUser?._id != null ? String(activeUser._id) : "";

  const allCoaches = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    if (loggedInCoachId) {
      seen.add(loggedInCoachId);
      const myName =
        String(activeUser?.name ?? "").trim() ||
        [activeUser?.firstName, activeUser?.lastName]
          .filter(Boolean)
          .join(" ") ||
        "Coach";
      out.push({
        id: loggedInCoachId,
        name: myName,
        avatarUrl: getAvatarUrl(activeUser, false, theme, theme !== "dark"),
      });
    }
    for (const c of orgCoaches) {
      const id = String(c?._id ?? c?.id ?? "");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        name: String(c.name ?? "").trim() || "Coach",
        avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
      });
    }
    for (const ev of allEvents) {
      const list = Array.isArray(ev.raw?.coaches) ? ev.raw.coaches : [];
      for (const entry of list) {
        const c = entry?.coachId ?? entry?.coach ?? entry;
        if (!c || typeof c !== "object") continue;
        const id = String(c._id ?? c.id ?? "");
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
          id,
          name: String(c.name ?? "").trim() || "Coach",
          avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
        });
      }
      for (const c of [ev.raw?.coach, ev.raw?.instructor]) {
        if (!c || typeof c !== "object") continue;
        const id = String(c._id ?? c.id ?? "");
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
          id,
          name: String(c.name ?? "").trim() || "Coach",
          avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
        });
      }
    }
    return out;
  }, [allEvents, orgCoaches, theme, activeUser, loggedInCoachId]);

  // A Team Coach has no personal bookings/calendar of their own — defaulting
  // the filter to just them would make the whole organization's schedule
  // look empty on first load. Leave the filter empty (= every coach) instead,
  // same as "Reset filters" already needs to for them.
  const isTeamCoach = locals.organizationRoleName === "Team Coach";

  React.useEffect(() => {
    if (coachDefaultSeeded) return;
    if (isTeamCoach) {
      setCoachDefaultSeeded(true);
      return;
    }
    if (!loggedInCoachId) return;
    setFilterCoachIds(new Set([loggedInCoachId]));
    setCoachDefaultSeeded(true);
  }, [loggedInCoachId, coachDefaultSeeded, isTeamCoach]);

  const visibleEvents = React.useMemo(() => {
    if (
      filterKinds.size === 0 &&
      filterCoachIds.size === 0 &&
      filterEventIds.size === 0 &&
      filterStatuses.size === 0
    )
      return allEvents;
    return allEvents.filter((ev) => {
      if (filterKinds.size > 0 && !filterKinds.has(ev.kind)) return false;
      if (filterEventIds.size > 0) {
        const id = String(ev.raw?._id ?? ev.raw?.id ?? "");
        if (!filterEventIds.has(id)) return false;
      }
      if (!eventMatchesCoachFilter(ev.kind, ev.raw, filterCoachIds)) {
        return false;
      }
      if (filterStatuses.size > 0) {
        if (filterStatuses.has("all")) return true;
        const day = startOfLocalDay(ev.start);
        const isBlocked =
          ev.kind === "blackout_summary" ||
          !!(
            findBlackoutOverrideForEvent(ev, day, overrides) ||
            findCoachBlackoutForEvent(ev, day, coachBlackouts)
          );
        const enrolled =
          typeof ev.raw?.enrolled_count === "number"
            ? ev.raw.enrolled_count
            : 0;
        const capacity = pickClassCapacity(ev.raw);
        const isBooked = !isBlocked && enrolled > 0;
        const isFull =
          !isBlocked &&
          capacity != null &&
          capacity > 0 &&
          enrolled >= capacity;
        const isAvailable = !isBlocked && !isBooked;
        let match = false;
        if (filterStatuses.has("booked") && isBooked) match = true;
        if (filterStatuses.has("full") && isFull) match = true;
        if (filterStatuses.has("availableHours") && isAvailable) match = true;
        if (filterStatuses.has("blockedTime") && isBlocked) match = true;
        if (!match) return false;
      }
      return true;
    });
  }, [
    allEvents,
    filterKinds,
    filterCoachIds,
    filterEventIds,
    filterStatuses,
    overrides,
    coachBlackouts,
  ]);

  const days = React.useMemo(
    () => groupEventsByDay(visibleEvents, weekStart),
    [visibleEvents, weekStart]
  );

  // Lists are org-wide; only bookables matching the coach filter may emit
  // blackout summary strips, otherwise every coach's blackouts would show.
  const loadedBookableIds = React.useMemo(() => {
    const s = new Set();
    const addAll = (kind, list) => {
      for (const item of list) {
        const id = item._id ?? item.id;
        if (!id) continue;
        if (!eventMatchesCoachFilter(kind, item, filterCoachIds)) continue;
        s.add(String(id));
      }
    };
    addAll("class", classes);
    addAll("pt", ptSessions);
    addAll("facility", facilities);
    return s;
  }, [classes, ptSessions, facilities, filterCoachIds]);

  const displayDays = React.useMemo(
    () =>
      days.map(({ date, events }) => ({
        date,
        events: mergeDayEventsWithBlackouts(
          events,
          date,
          overrides,
          loadedBookableIds,
          coachBlackouts,
          filterCoachIds
        ),
      })),
    [days, overrides, loadedBookableIds, coachBlackouts, filterCoachIds]
  );

  const dayViewMerged = React.useMemo(() => {
    const dayD = new Date(weekAnchor);
    dayD.setHours(0, 0, 0, 0);
    const dayFiltered = visibleEvents.filter((e) => eventOnDay(e, dayD));

    // Expand PT and facility events into individual bookable slots when a slot
    // duration is available — classes are already slot-sized.
    const slotExpanded = [];
    for (const ev of dayFiltered) {
      if (ev.kind === "class") {
        slotExpanded.push(ev);
        continue;
      }
      const slotMin = pickSlotDurationMinutes(ev.raw);
      if (!slotMin) {
        slotExpanded.push(ev);
        continue;
      }
      let slotStart = new Date(ev.start);
      let idx = 0;
      while (slotStart.getTime() + slotMin * 60000 <= ev.end.getTime()) {
        const slotEnd = new Date(slotStart.getTime() + slotMin * 60000);
        slotExpanded.push({
          ...ev,
          key: `${ev.key}-slot-${idx}`,
          start: new Date(slotStart),
          end: slotEnd,
        });
        slotStart = slotEnd;
        idx++;
      }
    }

    const sorted = slotExpanded.sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    );
    return mergeDayEventsWithBlackouts(
      sorted,
      dayD,
      overrides,
      loadedBookableIds,
      coachBlackouts,
      filterCoachIds
    );
  }, [
    visibleEvents,
    weekAnchor,
    overrides,
    loadedBookableIds,
    coachBlackouts,
    filterCoachIds,
  ]);

  const dayViewSingleDay = React.useMemo(() => {
    const hasBlackout = dayViewMerged.some(
      (r) =>
        r.kind === "blackout_summary" ||
        (!r.singleEventBlock && (r.blackoutOverride || r.coachBlackout))
    );
    const rows = dayViewMerged.filter((r) => r.kind !== "blackout_summary");
    return { hasBlackout, rows };
  }, [dayViewMerged]);

  const dayViewDateMidnight = React.useMemo(() => {
    const d = new Date(weekAnchor);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [weekAnchor]);

  const statsSourceEvents = React.useMemo(() => {
    if (view === "day") {
      const d = new Date(weekAnchor);
      d.setHours(0, 0, 0, 0);
      return visibleEvents.filter((e) => eventOnDay(e, d));
    }
    if (view === "month") {
      const y = weekAnchor.getFullYear();
      const m = weekAnchor.getMonth();
      return visibleEvents.filter(
        (e) => e.start.getFullYear() === y && e.start.getMonth() === m
      );
    }
    return visibleEvents.filter((e) => {
      const t = e.start.getTime();
      return t >= weekStart.getTime() && t <= weekEnd.getTime();
    });
  }, [view, visibleEvents, weekAnchor, weekStart, weekEnd]);

  const stats = React.useMemo(() => {
    const classN = statsSourceEvents.filter((e) => e.kind === "class").length;
    const ptN = statsSourceEvents.filter((e) => e.kind === "pt").length;
    const facilityN = statsSourceEvents.filter(
      (e) => e.kind === "facility"
    ).length;
    const blocked = statsSourceEvents.filter((e) => {
      const day = startOfLocalDay(e.start);
      return (
        findBlackoutOverrideForEvent(e, day, overrides) ||
        findCoachBlackoutForEvent(e, day, coachBlackouts)
      );
    }).length;
    return {
      bookings: statsSourceEvents.length,
      classes: classN,
      pt: ptN,
      facilities: facilityN,
      blocked,
    };
  }, [statsSourceEvents, overrides, coachBlackouts]);

  const monthTitle = weekStart.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const monthNavTitle = weekAnchor.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const toolbarDateLabel =
    view === "day"
      ? formatDayToolbarLabel(weekAnchor)
      : view === "month"
        ? monthNavTitle
        : monthTitle;

  const goToday = () => setWeekAnchor(new Date());
  const onPickToolbarDate = React.useCallback((dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return;
    const m = moment(dateStr, DATE_PICKER_FORMAT, true);
    if (!m.isValid()) return;
    const d = m.toDate();
    d.setHours(0, 0, 0, 0);
    setWeekAnchor(d);
  }, []);
  const goPrev = () => {
    const x = new Date(weekAnchor);
    if (view === "day") x.setDate(x.getDate() - 1);
    else if (view === "month") x.setMonth(x.getMonth() - 1);
    else x.setDate(x.getDate() - 7);
    setWeekAnchor(x);
  };
  const goNext = () => {
    const x = new Date(weekAnchor);
    if (view === "day") x.setDate(x.getDate() + 1);
    else if (view === "month") x.setMonth(x.getMonth() + 1);
    else x.setDate(x.getDate() + 7);
    setWeekAnchor(x);
  };

  const monthGrid = React.useMemo(
    () => buildMonthGrid(weekAnchor),
    [weekAnchor]
  );

  const isToday = (d) => sameDay(d, new Date());

  return (
    <div className="scheduling-overview scheduling-overview-calendar">
      <header className="scheduling-overview-cal-header">
        <hgroup>
          <h1 className="scheduling-overview-cal-title">Overview</h1>
          <span className="scheduling-overview-cal-badge">
            {view === "month"
              ? "Monthly"
              : view === "week"
                ? "Weekly"
                : "Daily"}
          </span>
        </hgroup>
      </header>

      <section className="scheduling-overview-stats" aria-label="Summary">
        <div
          role="button"
          tabIndex={0}
          className={`scheduling-overview-stat-card scheduling-overview-stat-card--bookings${filterKinds.size === 0 ? " is-active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => applyOverviewKindTab(null)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") applyOverviewKindTab(null);
          }}
        >
          <span className="scheduling-overview-stat-card-icon-wrap" aria-hidden>
            <img
              src="/img/new-icons/vuesax-linear-calendar-schedule.svg"
              alt=""
            />
          </span>
          <div className="scheduling-overview-stat-card-body">
            <span className="scheduling-overview-stat-label">Total events</span>
            <span className="scheduling-overview-stat-value">
              {loading ? "—" : stats.bookings}
            </span>
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          className={`scheduling-overview-stat-card scheduling-overview-stat-card--classes${filterKinds.has("class") ? " is-active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => applyOverviewKindTab("class")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ")
              applyOverviewKindTab("class");
          }}
        >
          <span className="scheduling-overview-stat-card-icon-wrap" aria-hidden>
            <img src="/img/new-icons/classes-icon.svg" alt="" />
          </span>
          <div className="scheduling-overview-stat-card-body">
            <span className="scheduling-overview-stat-label">Classes</span>
            <span className="scheduling-overview-stat-value">
              {loading ? "—" : stats.classes}
            </span>
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          className={`scheduling-overview-stat-card scheduling-overview-stat-card--pt${filterKinds.has("pt") ? " is-active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => applyOverviewKindTab("pt")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") applyOverviewKindTab("pt");
          }}
        >
          <span className="scheduling-overview-stat-card-icon-wrap" aria-hidden>
            <img src="/img/new-icons/pt-session-icon.svg" alt="" />
          </span>
          <div className="scheduling-overview-stat-card-body">
            <span className="scheduling-overview-stat-label">PT Sessions</span>
            <span className="scheduling-overview-stat-value">
              {loading ? "—" : stats.pt}
            </span>
          </div>
        </div>
        {hasNonPullScheduling && (
          <div
            role="button"
            tabIndex={0}
            className={`scheduling-overview-stat-card scheduling-overview-stat-card--facility${filterKinds.has("facility") ? " is-active" : ""}`}
            style={{ cursor: "pointer" }}
            onClick={() => applyOverviewKindTab("facility")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                applyOverviewKindTab("facility");
            }}
          >
            <span
              className="scheduling-overview-stat-card-icon-wrap"
              aria-hidden
            >
              <img src="/img/new-icons/facilities-icon.svg" alt="" />
            </span>
            <div className="scheduling-overview-stat-card-body">
              <span className="scheduling-overview-stat-label">Facilities</span>
              <span className="scheduling-overview-stat-value">
                {loading ? "—" : stats.facilities}
              </span>
            </div>
          </div>
        )}
        <div
          role="button"
          tabIndex={0}
          className={`scheduling-overview-stat-card scheduling-overview-stat-card--blocked${filterBlocked ? " is-active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => selectStatus("blockedTime")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") selectStatus("blockedTime");
          }}
        >
          <span className="scheduling-overview-stat-card-icon-wrap" aria-hidden>
            <img src="/img/new-icons/slash.svg" alt="" />
          </span>
          <div className="scheduling-overview-stat-card-body">
            <span className="scheduling-overview-stat-label">Blocked time</span>
            <span className="scheduling-overview-stat-value">
              {loading ? "—" : stats.blocked}
            </span>
          </div>
        </div>
      </section>

      <div className="scheduling-overview-toolbar">
        <div className="scheduling-overview-toolbar-left">
          <div className="scheduling-overview-toolbar-nav">
            <button type="button" data-button-type="outline" onClick={goToday}>
              Today
            </button>
            <div
              className="scheduling-overview-toolbar-date"
              title="Go to date"
            >
              <DateInput
                id="scheduling-overview-date-input"
                name="schedulingOverviewDate"
                value={weekAnchor}
                onChange={onPickToolbarDate}
                customClassNames="scheduling-overview-date-input"
                dontPadding
                datePlaceholder=""
                highlightTodayInCalendar
                aria-label="Choose date to show"
              />
              <span
                className="scheduling-overview-calendar-icon-layer"
                aria-hidden
              >
                <SchedulingToolbarCalendarIcon />
              </span>
            </div>
            <div className="scheduling-overview-toolbar-arrows">
              <button
                type="button"
                className="scheduling-overview-nav-btn"
                aria-label={
                  view === "day"
                    ? "Previous day"
                    : view === "month"
                      ? "Previous month"
                      : "Previous week"
                }
                onClick={goPrev}
              >
                <img src="/img/new-icons/arrow-left.svg" alt="" />
              </button>
              <button
                type="button"
                className="scheduling-overview-nav-btn"
                aria-label={
                  view === "day"
                    ? "Next day"
                    : view === "month"
                      ? "Next month"
                      : "Next week"
                }
                onClick={goNext}
              >
                <img src="/img/new-icons/arrow-right.svg" alt="" />
              </button>
            </div>
          </div>
          <span className="scheduling-overview-range-label">
            {toolbarDateLabel}
          </span>
        </div>
        <div className="scheduling-overview-toolbar-right">
          <div className="scheduling-overview-view-toggle">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                type="button"
                className={
                  view === v
                    ? "scheduling-overview-view-btn is-active"
                    : "scheduling-overview-view-btn"
                }
                onClick={() => setView(v)}
              >
                {v === "month" ? "Month" : v === "week" ? "Week" : "Day"}
              </button>
            ))}
          </div>
          <div
            ref={statusMenuRef}
            className={
              statusMenuOpen
                ? "scheduling-overview-status-filter is-open"
                : "scheduling-overview-status-filter"
            }
          >
            {(() => {
              const statusFilterOptions = [
                { id: "booked", label: "Booked" },
                { id: "full", label: "Fully booked" },
                { id: "availableHours", label: "Available hours" },
                { id: "blockedTime", label: "Blocked time" },
                { id: "all", label: "All" },
              ];
              const selectedOptions = statusFilterOptions.filter((o) =>
                filterStatuses.has(o.id)
              );
              const triggerLabel =
                selectedOptions.length === 0
                  ? "All"
                  : selectedOptions.length === 1
                    ? selectedOptions[0].label
                    : `${selectedOptions.length} selected`;
              return (
                <>
                  <button
                    type="button"
                    className="scheduling-overview-filter-btn"
                    aria-haspopup="menu"
                    aria-expanded={statusMenuOpen}
                    onClick={() => setStatusMenuOpen((o) => !o)}
                  >
                    <img
                      src="/img/new-icons/scheduling-calendar-icon.svg"
                      alt=""
                      aria-hidden="true"
                    />
                    {triggerLabel}
                    {filterStatuses.size > 1 && (
                      <span className="scheduling-overview-filter-badge">
                        {filterStatuses.size}
                      </span>
                    )}
                    <img
                      src={
                        statusMenuOpen
                          ? "/img/svg/angle-up.svg"
                          : "/img/svg/angle-down.svg"
                      }
                      alt=""
                      className="scheduling-overview-status-filter-chevron"
                    />
                  </button>
                  <ul
                    className="scheduling-overview-status-filter-menu"
                    role="menu"
                  >
                    {statusFilterOptions.map((s) => {
                      const checked = filterStatuses.has(s.id);
                      return (
                        <li key={s.id} role="none">
                          <label className="scheduling-overview-status-filter-item">
                            <input
                              type="radio"
                              className="scheduling-overview-status-filter-checkbox"
                              name="scheduling-overview-status"
                              checked={checked}
                              onChange={() => selectStatus(s.id)}
                            />
                            <img
                              src={
                                checked
                                  ? "/img/svg/vuesax-linear-tick-square-checked.svg"
                                  : "/img/new-icons/unchecked-square.svg"
                              }
                              alt=""
                              className={
                                checked
                                  ? "scheduling-overview-status-filter-check-icon is-checked"
                                  : "scheduling-overview-status-filter-check-icon"
                              }
                            />
                            <span>{s.label}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </>
              );
            })()}
          </div>
          <div
            ref={createEventMenuRef}
            className={
              createEventMenuOpen
                ? "scheduling-overview-create-event is-open"
                : "scheduling-overview-create-event"
            }
          >
            <button
              type="button"
              className="scheduling-overview-create-event-btn"
              aria-haspopup="menu"
              aria-expanded={createEventMenuOpen}
              onClick={() => setCreateEventMenuOpen((o) => !o)}
            >
              <img
                src="/img/new-icons/add.svg"
                alt=""
                className="scheduling-overview-create-event-btn-icon"
              />
              <span>Create Event</span>
              <img
                src={
                  createEventMenuOpen
                    ? "/img/svg/angle-up.svg"
                    : "/img/svg/angle-down.svg"
                }
                alt=""
                className="scheduling-overview-create-event-btn-chevron"
              />
            </button>
            <ul className="scheduling-overview-create-event-menu">
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="scheduling-overview-create-event-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setCreateEventMenuOpen(false);
                    setSlotModal({ kind: "class", initialRecord: null });
                  }}
                >
                  <span
                    className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--class"
                    aria-hidden
                  >
                    <img src="/img/new-icons/classes-icon.svg" alt="" />
                  </span>
                  <span>Create Class</span>
                </button>
              </li>
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="scheduling-overview-create-event-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setCreateEventMenuOpen(false);
                    setSlotModal({ kind: "pt", initialRecord: null });
                  }}
                >
                  <span
                    className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--pt"
                    aria-hidden
                  >
                    <img src="/img/new-icons/pt-session-icon.svg" alt="" />
                  </span>
                  <span>Create PT Session</span>
                </button>
              </li>
              {hasNonPullScheduling && (
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="scheduling-overview-create-event-item"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setCreateEventMenuOpen(false);
                      setSlotModal({ kind: "facility", initialRecord: null });
                    }}
                  >
                    <span
                      className="scheduling-overview-create-event-item-icon scheduling-overview-create-event-item-icon--facility"
                      aria-hidden
                    >
                      <img src="/img/new-icons/facilities-icon.svg" alt="" />
                    </span>
                    <span>Create Facility</span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <p className="scheduling-overview-error" role="alert">
          {error}
        </p>
      )}

      {previewNotice && (
        <p className="scheduling-overview-preview-notice" aria-live="polite">
          {previewNotice}
          <button
            type="button"
            className="scheduling-overview-preview-notice-dismiss"
            onClick={() => setPreviewNotice(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </p>
      )}

      {loading && <OverviewCalendarSkeleton theme={theme} />}

      {!loading && view === "week" && (
        <div className="scheduling-overview-week-wrap">
          <div className="scheduling-overview-week-head">
            {displayDays.map(({ date }, i) => (
              <div
                key={date.toISOString()}
                className={
                  "scheduling-overview-day-head" +
                  (isToday(date) ? " is-today" : "")
                }
              >
                <div className="scheduling-overview-day-head-inner">
                  <span className="scheduling-overview-day-name">
                    {WEEKDAYS[i]}
                  </span>
                  <span className="scheduling-overview-day-num">
                    {date.getDate()}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="scheduling-overview-week-grid">
            {displayDays.map(({ date, events: dayEvents }) => (
              <div
                key={date.toISOString()}
                className={
                  "scheduling-overview-day-column" +
                  (isToday(date) ? " is-today" : "")
                }
              >
                <div
                  className={
                    "scheduling-overview-day-canvas scheduling-overview-day-canvas--stacked" +
                    (dayEvents.some(
                      (r) =>
                        r.kind === "blackout_summary" ||
                        (!r.singleEventBlock &&
                          (r.blackoutOverride || r.coachBlackout))
                    )
                      ? " scheduling-overview-day-canvas--blackout-tint"
                      : "")
                  }
                >
                  {dayEvents.length === 0 && (
                    <span className="scheduling-overview-empty-day">
                      No events scheduled
                    </span>
                  )}
                  {dayEvents.map((row) => (
                    <SchedulingOverviewStackedRow
                      key={row.key}
                      row={row}
                      activeUser={activeUser}
                      theme={theme}
                      coachAvatarSrc={coachAvatarSrc}
                      previewFocusKey={previewFocusKey}
                      onEventClick={handleEventClick}
                      tz={gymTimezone}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && view === "day" && (
        <div className="scheduling-overview-day-view">
          <div
            className={
              "scheduling-overview-day-view-card" +
              (dayViewSingleDay.hasBlackout
                ? " scheduling-overview-day-view-card--blackout"
                : "") +
              (isToday(weekAnchor) ? " is-today" : "")
            }
          >
            <div className="scheduling-overview-day-canvas scheduling-overview-day-canvas--day-timeline">
              <SchedulingOverviewDayTimeline
                dayDate={dayViewDateMidnight}
                timelineRows={dayViewSingleDay.rows}
                mergedRows={dayViewMerged}
                blockedCount={stats.blocked}
                activeUser={activeUser}
                theme={theme}
                coachAvatarSrc={coachAvatarSrc}
                previewFocusKey={previewFocusKey}
                onEventClick={handleEventClick}
                onEventEdit={handleEditEvent}
                onSlotCreate={handleSlotCreate}
                hasNonPullScheduling={hasNonPullScheduling}
                tz={gymTimezone}
              />
            </div>
          </div>
        </div>
      )}

      {!loading && view === "month" && (
        <div className="scheduling-overview-month-wrap">
          <div className="scheduling-overview-month-weekdays" aria-hidden>
            {WEEKDAYS.map((wd) => (
              <div key={wd} className="scheduling-overview-month-weekday">
                {wd}
              </div>
            ))}
          </div>
          <section
            className="scheduling-overview-month-grid"
            aria-label={`Calendar ${monthNavTitle}`}
          >
            {monthGrid.map(({ date: cellDate, inMonth }) => {
              const dayEvents = visibleEvents
                .filter((e) => eventOnDay(e, cellDate))
                .sort((a, b) => a.start.getTime() - b.start.getTime());
              const visible = dayEvents.slice(0, MONTH_PILL_MAX);
              const moreCount = dayEvents.length - visible.length;
              const cellDay = startOfLocalDay(cellDate);
              const blackoutSummaries = getCollapsedBlackoutSummariesForDay(
                cellDay,
                overrides,
                loadedBookableIds,
                coachBlackouts,
                filterCoachIds
              );
              const fullDayBlackoutRow = blackoutSummaries.find(
                (s) => s.kind === "blackout_summary" && s.fullDayOnly
              );
              const hasFullDayBlackout = Boolean(fullDayBlackoutRow);
              const partialBlackouts = hasFullDayBlackout
                ? []
                : blackoutSummaries.filter(
                    (s) => s.kind === "blackout_summary" && !s.fullDayOnly
                  );
              const fullDayBadgeTitle = formatFullDayBlackoutBadgeTitle(
                fullDayBlackoutRow,
                gymTimezone
              );
              const mergedDayRows = mergeDayEventsWithBlackouts(
                dayEvents,
                cellDay,
                overrides,
                loadedBookableIds,
                coachBlackouts,
                filterCoachIds
              );
              const mergedEventByKey = new Map();
              for (const row of mergedDayRows) {
                if (row.kind === "blackout_summary") continue;
                mergedEventByKey.set(row.key, row);
              }
              const buildPillRow = (ev) => {
                const ann = mergedEventByKey.get(ev.key);
                const monthPillSingleEvent = ann
                  ? Boolean(ann.singleEventBlock)
                  : eventHasSingleEventBlock(
                      ev,
                      cellDay,
                      overrides,
                      coachBlackouts
                    );
                const monthPillBlackout = ann
                  ? Boolean(
                      !ann.singleEventBlock &&
                        (ann.blackoutOverride || ann.coachBlackout)
                    )
                  : !monthPillSingleEvent &&
                    (findBlackoutOverrideForEvent(ev, cellDay, overrides) ||
                      findCoachBlackoutForEvent(ev, cellDay, coachBlackouts));
                return { ev, monthPillSingleEvent, monthPillBlackout };
              };
              const monthPillRows = visible.map(buildPillRow);
              const allMonthPillRows =
                moreCount > 0 ? dayEvents.map(buildPillRow) : monthPillRows;
              return (
                <div
                  key={cellDate.toISOString()}
                  className={
                    "scheduling-overview-month-cell" +
                    (!inMonth ? " is-other-month" : "") +
                    (isToday(cellDate) ? " is-today" : "") +
                    (hasFullDayBlackout
                      ? " scheduling-overview-month-cell--full-blackout"
                      : "")
                  }
                >
                  <div className="scheduling-overview-month-cell-head">
                    <span className="scheduling-overview-month-day-num">
                      {cellDate.getDate()}
                    </span>
                    {hasFullDayBlackout ? (
                      <span
                        className="scheduling-overview-month-full-blackout-badge"
                        title={fullDayBadgeTitle}
                      >
                        Blacked out
                      </span>
                    ) : null}
                  </div>
                  <div className="scheduling-overview-month-cell-events">
                    {monthPillRows.map(
                      (
                        { ev, monthPillSingleEvent, monthPillBlackout },
                        pillIndex
                      ) => {
                        const prevBlackout =
                          pillIndex > 0 &&
                          monthPillRows[pillIndex - 1].monthPillBlackout;
                        const nextBlackout =
                          pillIndex < monthPillRows.length - 1 &&
                          monthPillRows[pillIndex + 1].monthPillBlackout;
                        const isLastVisiblePill =
                          pillIndex === monthPillRows.length - 1;
                        /** Last visible row: bleed frame into cell bottom padding (full column). */
                        const canFlushBottom = isLastVisiblePill;
                        const blackoutFrameClass = monthPillBlackout
                          ? `scheduling-overview-month-blackout-pill-frame${
                              pillIndex === 0
                                ? " scheduling-overview-month-blackout-pill-frame--flush-top"
                                : ""
                            }${
                              canFlushBottom
                                ? " scheduling-overview-month-blackout-pill-frame--flush-bottom"
                                : ""
                            }${
                              prevBlackout
                                ? " scheduling-overview-month-blackout-pill-frame--stacked"
                                : ""
                            }${
                              nextBlackout
                                ? " scheduling-overview-month-blackout-pill-frame--stacked-next"
                                : ""
                            }`
                          : "";
                        return (
                          <div
                            key={ev.key}
                            className={
                              monthPillBlackout
                                ? blackoutFrameClass
                                : "scheduling-overview-month-pill-slot"
                            }
                          >
                            <button
                              type="button"
                              data-scheduling-preview-key={ev.key}
                              onClick={() =>
                                handleEventClick(
                                  monthPillSingleEvent
                                    ? { ...ev, singleEventBlock: true }
                                    : ev
                                )
                              }
                              className={
                                "scheduling-overview-month-pill" +
                                overviewMonthPillClass(ev.kind) +
                                (previewFocusKey != null &&
                                ev.key === previewFocusKey
                                  ? " scheduling-overview-month-pill--preview-focus"
                                  : "") +
                                (monthPillSingleEvent
                                  ? " scheduling-overview-month-pill--single-event-block"
                                  : "") +
                                (monthPillBlackout
                                  ? " scheduling-overview-month-pill--blackout"
                                  : "")
                              }
                              title={`${formatAmPm(ev.start, ev.raw?.timezone)} – ${formatAmPm(ev.end, ev.raw?.timezone)} · ${ev.title}`}
                            >
                              <div
                                className={
                                  "scheduling-overview-month-pill-body" +
                                  (monthPillSingleEvent
                                    ? " scheduling-overview-month-pill-body--blocked-row"
                                    : "")
                                }
                              >
                                <span className="scheduling-overview-month-pill-label">
                                  {ev.title}
                                </span>
                                {monthPillSingleEvent ? (
                                  <span className="scheduling-overview-month-pill-blocked-tag">
                                    Blocked
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          </div>
                        );
                      }
                    )}
                    {moreCount > 0 && (
                      <button
                        type="button"
                        className="scheduling-overview-month-more"
                        onClick={(e) =>
                          setExpandedMonthDay({
                            date: cellDate,
                            pillRows: allMonthPillRows,
                            anchorRect: e.currentTarget.getBoundingClientRect(),
                          })
                        }
                      >
                        +{moreCount} more
                      </button>
                    )}
                    {partialBlackouts.map((row) => (
                      <MonthBlackoutInfoPopover
                        key={row.key}
                        row={row}
                        tz={gymTimezone}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      )}

      {expandedMonthDay && (
        <MonthDayExpandedPopover
          date={expandedMonthDay.date}
          pillRows={expandedMonthDay.pillRows}
          anchorRect={expandedMonthDay.anchorRect}
          onClose={() => setExpandedMonthDay(null)}
          onEventClick={handleEventClick}
          previewFocusKey={previewFocusKey}
        />
      )}

      {filtersSlotEl &&
        createPortal(
          <SchedulingSidebarFilters
            eventsByKind={eventsByKind}
            coaches={allCoaches}
            filterKinds={filterKinds}
            filterEventIds={filterEventIds}
            filterCoachIds={filterCoachIds}
            setFilterKinds={setFilterKinds}
            setFilterEventIds={setFilterEventIds}
            setFilterCoachIds={setFilterCoachIds}
            defaultCoachId={isTeamCoach ? "" : loggedInCoachId}
            hasNonPullScheduling={hasNonPullScheduling}
          />,
          filtersSlotEl
        )}

      {markAttendanceEvent && (
        <div className="scheduling-overview-mark-attendance-overlay">
          {markAttendanceEvent.kind === "pt" ||
          markAttendanceEvent.type === "pt" ? (
            <MarkAttendancePtView
              event={markAttendanceEvent}
              onBack={() => setMarkAttendanceEvent(null)}
              onEdit={(ev) => {
                if (!ev?.raw) return;
                handleEditEvent(
                  ev.kind ? ev : { ...ev, kind: ev.kind ?? "pt" }
                );
              }}
              theme={theme}
            />
          ) : (
            <MarkAttendanceView
              event={markAttendanceEvent}
              onBack={() => setMarkAttendanceEvent(null)}
              onEdit={handleEditEvent}
              theme={theme}
            />
          )}
        </div>
      )}

      <EventDetailsPanel
        event={selectedEvent}
        activeUser={activeUser}
        theme={theme}
        onClose={closeEventPanel}
        onEdit={handleEditEvent}
        onBlockTime={handleBlockTimeFromPanel}
        onBlockThisEvent={handleBlockThisEvent}
        onUnblockEvent={handleUnblockEvent}
        onMarkAttendance={handleMarkAttendance}
        members={bookingsForPanel}
        membersLoading={bookingsForPanelLoading}
        canManage={canManageOverviewEvent(selectedEvent, activeUser)}
      />

      <CreateBlockTimeModal
        key={
          blockTimeModalOpen && blockTimePreset
            ? `bt-${blockTimePreset.scheduleKind}-${blockTimePreset.bookableId}`
            : "block-time-closed-overview"
        }
        isOpen={blockTimeModalOpen}
        classes={classes}
        ptSessions={ptSessions}
        facilities={facilities}
        showFacilities={hasNonPullScheduling}
        onClose={() => {
          setBlockTimeModalOpen(false);
          setBlockTimePreset(null);
        }}
        initialScheduleKind={blockTimePreset?.scheduleKind ?? null}
        initialPreselectedBookableId={blockTimePreset?.bookableId ?? null}
        initialScrollSection={BLOCK_TIME_SCROLL_SELECT_DATE}
        onConfirm={async (payloads) => {
          await submitBlockTimePayloads(payloads);
          setBlockTimeModalOpen(false);
          setBlockTimePreset(null);
          loadData();
        }}
      />

      <BlockTimeFromDetailsModal
        isOpen={!!blockDetailsModalEvent}
        event={blockDetailsModalEvent}
        theme={theme}
        onClose={() => setBlockDetailsModalEvent(null)}
        onConfirm={() => {
          setBlockDetailsModalEvent(null);
          setSelectedEvent(null);
          loadData();
        }}
      />

      <CreateClassModal
        isOpen={slotModal?.kind === "class"}
        initialRecord={
          slotModal?.kind === "class" ? slotModal.initialRecord : null
        }
        showAccessPermissions={hasNonPullScheduling}
        hasPressScheduling={hasPressScheduling}
        hasPullScheduling={hasPullScheduling}
        canPushUser={canPushUser}
        canPullUser={canPullUser}
        onClose={() => setSlotModal(null)}
        onConfirm={async (payload, fileOptions = {}) => {
          const editId =
            slotModal?.initialRecord?._id ?? slotModal?.initialRecord?.id;
          const files = fileOptions.thumbnailFile
            ? { thumbnail: fileOptions.thumbnailFile }
            : null;
          const res = await fetchSchedulingBookable(
            editId
              ? `/actions/scheduling/classes/${encodeURIComponent(String(editId))}`
              : "/actions/scheduling/classes",
            editId ? "PUT" : "POST",
            payload,
            files
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (editId ? "Failed to update class" : "Failed to create class")
            );
          }
          setSlotModal(null);
          loadData();
        }}
      />

      <CreatePtSessionModal
        isOpen={slotModal?.kind === "pt"}
        initialRecord={
          slotModal?.kind === "pt" ? slotModal.initialRecord : null
        }
        showAccessPermissions={hasNonPullScheduling}
        hasPressScheduling={hasPressScheduling}
        hasPullScheduling={hasPullScheduling}
        canPushUser={canPushUser}
        canPullUser={canPullUser}
        onClose={() => setSlotModal(null)}
        onConfirm={async (payload) => {
          const editId =
            slotModal?.initialRecord?._id ?? slotModal?.initialRecord?.id;
          const res = await fetch(
            editId
              ? `/actions/scheduling/pt-sessions/${encodeURIComponent(String(editId))}`
              : "/actions/scheduling/pt-sessions",
            {
              method: editId ? "PUT" : "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "same-origin",
              body: JSON.stringify(payload),
            }
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (editId
                  ? "Failed to update PT session"
                  : "Failed to create PT session")
            );
          }
          setSlotModal(null);
          loadData();
        }}
      />

      <CreateFacilityModal
        isOpen={slotModal?.kind === "facility"}
        hasPressScheduling={hasPressScheduling}
        initialRecord={
          slotModal?.kind === "facility" ? slotModal.initialRecord : null
        }
        onClose={() => setSlotModal(null)}
        onConfirm={async (payload, fileOptions = {}) => {
          const editId =
            slotModal?.initialRecord?._id ?? slotModal?.initialRecord?.id;
          const res = await fetchSchedulingBookable(
            editId
              ? `/actions/scheduling/facilities/${encodeURIComponent(String(editId))}`
              : "/actions/scheduling/facilities",
            editId ? "PUT" : "POST",
            payload,
            fileOptions.files ?? null
          );
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(
              data.error ||
                (editId
                  ? "Failed to update facility"
                  : "Failed to create facility")
            );
          }
          setSlotModal(null);
          loadData();
        }}
      />
    </div>
  );
};

export default SchedulingOverviewCalendar;
